import { getBackendUrl } from '@/lib/backendUrl';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { stocktakeSessionsService, auditorsService } from '../services';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useOffline } from '../contexts/OfflineContext';
import stocktakeOfflineService, { SYNC_STATES } from '../services/stocktakeOfflineService';
import { calculateStockAuditLocally, validateStockAuditLocally, generateVerificationNotes } from '../services/localCalculationEngine';
import { subcategoryComparator } from '../lib/subcategoryOrder';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '@/components/ui/collapsible';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import {
  ClipboardList, ArrowLeft, Loader2, Save, Play, CheckCircle, Lock, Unlock,
  RotateCcw, MapPin, CalendarDays, User, FileText, AlertCircle, TrendingUp,
  Pencil, X, Calculator, DollarSign, BarChart3, Trash2, Plus,
  ChevronRight, ChevronLeft, ChevronDown, Send, Package, ShoppingCart, ArrowRightLeft,
  ClipboardCheck, AlertTriangle, Download, Upload, CloudUpload, WifiOff, HardDrive,
  FileDown, Tablet, ExternalLink, Camera, ZoomIn, MessageSquare, Search, PackagePlus, Settings2,
  RefreshCw, History, ShieldCheck
} from 'lucide-react';
import { format } from 'date-fns';
import { OpeningStockPanel, AdjustmentsPanel, StockAuditResultsPanel, ValidationReportPanel } from './StockAuditPanels';
import { WizardSalesPanel } from './WizardSalesPanel';
import { WizardDeliveriesCreditsPanel } from './WizardDeliveriesPanel';
import DeliveryDocumentImportPanel from '../components/DeliveryDocumentImportPanel';
import AddFromMasterModal from '../components/AddFromMasterModal';
import LocationBreadcrumb from '../components/LocationBreadcrumb';
import SiteInventorySetupModal from '../components/SiteInventorySetupModal';
import StocktakeReportsDialog from '../components/StocktakeReportsDialog';
import FindingsActionsDialog from '../components/FindingsActionsDialog';
import PreviousYTDPanel from './panels/PreviousYTDPanel';
import OfflinePackageDiagnostics from '../components/OfflinePackageDiagnostics';
import { ParentAuditDashboard } from './MultiLocationComponents';
import { CellarIssuesPanel, LocationTransfersPanel } from './LocationTransferPanels';
import { getApiError, formatDDMMYYYY } from '@/lib/utils';
import { getYieldHint } from '@/lib/productHelpers';
import { WizardShortcutHandler, WizardShortcutLegend } from '../components/WizardShortcuts';

const STATUS_CONFIG = {
  draft: { label: 'Draft', className: 'bg-slate-100 text-slate-700', icon: FileText },
  in_progress: { label: 'In Progress', className: 'bg-blue-100 text-blue-700', icon: Play },
  completed: { label: 'Completed', className: 'bg-green-100 text-green-700', icon: CheckCircle },
  locked: { label: 'Locked', className: 'bg-amber-100 text-amber-800', icon: Lock },
};

// ── Wizard Step Definitions ──
const AUDIT_STEPS = [
  { key: 'opening', label: 'Opening Stock', icon: Package, description: 'Carry forward or enter opening stock' },
  { key: 'deliveries', label: 'Deliveries & Credits', icon: ArrowRightLeft, description: 'Review deliveries and credit notes' },
  { key: 'closing', label: 'Closing Stock', icon: ClipboardCheck, description: 'Review counts and make targeted corrections' },
  { key: 'adjustments', label: 'Adjustments', icon: ClipboardList, description: 'Wastage, allowances, management' },
  { key: 'sales', label: 'Sales', icon: ShoppingCart, description: 'Review sales and cost of sales' },
  // iter345 — Previous YTD: auto-fills from prior session totals.
  { key: 'previous_ytd', label: 'Previous YTD', icon: TrendingUp, description: 'Auto-filled YTD totals; review or override' },
  { key: 'calculate', label: 'Calculate & Review', icon: Calculator, description: 'Run calculation, validate, and publish' },
];
const CELLAR_AUDIT_STEPS = [
  { key: 'opening', label: 'Opening Stock', icon: Package, description: 'Carry forward or enter opening stock' },
  { key: 'deliveries', label: 'Deliveries & Credits', icon: ArrowRightLeft, description: 'Review deliveries and credit notes' },
  { key: 'closing', label: 'Closing Stock', icon: ClipboardCheck, description: 'Review counts and make targeted corrections' },
  { key: 'issues', label: 'Issues to Locations', icon: ShoppingCart, description: 'Stock issued out and returns received' },
  // iter367 #7 — Adjustments removed from cellar: the cellar is just a
  // stock container, so wastage / allowances / management adjustments
  // belong at the consuming non-cellar location, not here.
  // iter420l — Calculate ADDED BACK to cellar steps. The cellar still
  // doesn't sell, so the result isn't a stocktake-ratio in the
  // sales sense — but the operator needs a place to run the
  // movement calc (Opening + Deliveries − Credits − Issues = Expected
  // Closing, compared to Actual Closing). Without it, the consolidated
  // review reports "1/2 of locations calculated" because the cellar
  // child has no calculation_snapshot. The Calculate step now writes
  // that snapshot so the cellar counts.
  { key: 'calculate', label: 'Calculate & Review', icon: Calculator, description: 'Cellar stock movement: Opening + Deliveries − Credits − Issues = Expected Closing' },
];
const LOCATION_AUDIT_STEPS = [
  { key: 'opening', label: 'Opening Stock', icon: Package, description: 'Carry forward or enter opening stock' },
  { key: 'closing', label: 'Closing Stock', icon: ClipboardCheck, description: 'Review counts and make targeted corrections' },
  { key: 'transfers', label: 'Transfers from Cellar', icon: ArrowRightLeft, description: 'Stock received from and returned to cellar' },
  { key: 'adjustments', label: 'Adjustments', icon: ClipboardList, description: 'Wastage, allowances, management' },
  // iter363 — Non-cellar (bar/restaurant) locations sell stock to
  // customers, so they need a Sales step the same as a single-site
  // audit. Previously this step was missing and bar managers had no
  // way to record sales for the audit period.
  { key: 'sales', label: 'Sales', icon: ShoppingCart, description: 'Review sales and cost of sales' },
  // iter420i — Previous YTD was missing from non-cellar location
  // wizards even though it IS the right place to capture cumulative
  // figures (cellar-child sessions don't sell, so YTD belongs at the
  // consuming location). Cellar children stay YTD-free per design.
  { key: 'previous_ytd', label: 'Previous YTD', icon: TrendingUp, description: 'Auto-filled YTD totals; review or override' },
  { key: 'calculate', label: 'Calculate & Review', icon: Calculator, description: 'Run calculation, validate, and publish' },
];
const VALUATION_STEPS = [
  { key: 'opening', label: 'Opening Stock', icon: Package, description: 'Carry forward or enter opening stock' },
  { key: 'closing', label: 'Closing Stock', icon: ClipboardCheck, description: 'Review counts and make targeted corrections' },
  // PSB Stage 5 writes BARSFILE FYTD values to session_previous_ytd for valuation sessions.
  // The step is needed so operators can review/fill held fields (estimated, management).
  { key: 'previous_ytd', label: 'Previous YTD', icon: TrendingUp, description: 'Auto-filled YTD totals; review or override' },
  { key: 'calculate', label: 'Calculate & Review', icon: Calculator, description: 'Run calculation and publish' },
];


// ── Progress Tracker ──
function WizardProgressBar({ steps, currentStep, onStepClick }) {
  return (
    <div className="flex items-center gap-0 overflow-x-auto pb-1" data-testid="wizard-progress-bar">
      {steps.map((step, idx) => {
        const isActive = idx === currentStep;
        const isCompleted = idx < currentStep;
        const Icon = step.icon;
        return (
          <React.Fragment key={step.key}>
            {idx > 0 && (
              <div className={`hidden sm:block h-px flex-1 min-w-[20px] ${isCompleted ? 'bg-emerald-400' : 'bg-slate-200'}`} />
            )}
            <button
              onClick={() => onStepClick(idx)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium transition-all shrink-0 ${
                isActive ? 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-200' :
                isCompleted ? 'text-emerald-700 hover:bg-emerald-50' :
                'text-slate-400 hover:text-slate-600 hover:bg-slate-50'
              }`}
              data-testid={`wizard-step-${step.key}`}
            >
              <span className={`flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold shrink-0 ${
                isActive ? 'bg-indigo-600 text-white' :
                isCompleted ? 'bg-emerald-500 text-white' :
                'bg-slate-200 text-slate-500'
              }`}>
                {isCompleted ? <CheckCircle className="h-3.5 w-3.5" /> : idx + 1}
              </span>
              <span className="hidden md:inline">{step.label}</span>
            </button>
          </React.Fragment>
        );
      })}
    </div>
  );
}


// ── Step Validation Messages ──
function StepValidation({ warnings, errors }) {
  if (!warnings?.length && !errors?.length) return null;
  return (
    <div className="space-y-2 mb-4" data-testid="step-validation">
      {errors?.map((e, i) => (
        <div key={`e-${i}`} className="flex items-start gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{e}</span>
        </div>
      ))}
      {warnings?.map((w, i) => (
        <div key={`w-${i}`} className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          <AlertCircle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
          <span>{w}</span>
        </div>
      ))}
    </div>
  );
}


export default function StocktakeSessionPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, isAdmin, isScheduler, isSeniorScheduler, isAuditor } = useAuth();
  // iter399 — any internal user (incl. auditor) can unlock / revert sessions.
  const isInternalUser = isAdmin || isScheduler || isSeniorScheduler || isAuditor;
  const { isOnline, refreshOfflineSessions, syncStocktakeSession } = useOffline();
  const [searchParams] = useSearchParams();
  const forceOffline = searchParams.get('offline') === 'true';
  const [networkOnline, setNetworkOnline] = useState(navigator.onLine);
  const isOfflineMode = forceOffline || !networkOnline;

  // Listen for real network state changes
  useEffect(() => {
    const goOnline = () => { console.log('[Wizard] Network: ONLINE'); setNetworkOnline(true); };
    const goOffline = () => { console.log('[Wizard] Network: OFFLINE'); setNetworkOnline(false); };
    window.addEventListener('online', goOnline);
    window.addEventListener('offline', goOffline);
    return () => { window.removeEventListener('online', goOnline); window.removeEventListener('offline', goOffline); };
  }, []);

  const [session, setSession] = useState(null);
  const [vatRate, setVatRate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState([]);
  const [entries, setEntries] = useState({});
  const [importedObservations, setImportedObservations] = useState([]);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  // iter593 OCC — set when the server rejects a save because a newer version
  // already exists on the server (HTTP 409 STALE_CONFLICT).
  const [conflictError, setConflictError] = useState(null);
  const [auditors, setAuditors] = useState([]);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [productMap, setProductMap] = useState({});
  // iter379 — "Refresh Data from Master" heal flow on stale snapshots
  // (CAPS legacy names, missing default units, £0 costs from products
  // that synced before master data was cleaned up).
  const [healing, setHealing] = useState(false);

  // Phase 2 — Actual Receipts
  const [accountsSales, setAccountsSales] = useState({
    accounts_sales_incl_vat: '',
    accounts_sales_excl_vat: '',
    accounts_sales_notes: '',
  });
  const accountsSalesSaveRef = useRef(null);

  // Offline state
  const [offlinePkg, setOfflinePkg] = useState(null);
  const [offlineSyncState, setOfflineSyncState] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [isAvailableOffline, setIsAvailableOffline] = useState(false);
  const [offlineCalcResult, setOfflineCalcResult] = useState(null);
  const [offlineMovementOpen, setOfflineMovementOpen] = useState(false);
  const [offlineProductOpen, setOfflineProductOpen] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [showDiagnostics, setShowDiagnostics] = useState(false);

  // Closing stock search/filter state
  const [closingSearch, setClosingSearch] = useState('');
  const [closingSelectedCategories, setClosingSelectedCategories] = useState([]);
  const [closingSelectedSubcategories, setClosingSelectedSubcategories] = useState([]);
  // iter364 — Closing-stock SPLIT counts. Some auditors count the same
  // product across multiple shelves / locations and want to keep each
  // count visible (e.g. bar 5 bottles + cellar 12 bottles = 17). To
  // avoid a DB schema change, we keep splits PURELY in the UI here:
  //   entrySplits[count_sheet_line_id] = [{ qty: '5', _key }, { qty: '12', _key }]
  // When splits exist for a line, the renderer shows N qty inputs and
  // pushes the SUM into entries[lineId].quantity via handleEntryChange.
  // Splits are cleared on save/reload (the server only knows the sum).
  const [entrySplits, setEntrySplits] = useState({});
  const [addFromMasterOpen, setAddFromMasterOpen] = useState(false);
  const [siteInventorySetupOpen, setSiteInventorySetupOpen] = useState(false);
  const [reportsDialogOpen, setReportsDialogOpen] = useState(false);
  // 2026-02 — single top-row Summary shortcut (replaces the old bottom
  // panel). Mirrors the exact same `summary_link` contract the Report
  // Centre dialog already uses (report_reconciliation_service.
  // resolve_report_centre_summary_state) so the two surfaces never
  // disagree — no new backend route, just read directly from the same
  // manifest endpoint.
  const [summaryLinkInfo, setSummaryLinkInfo] = useState(null);
  const [summaryLinkLoading, setSummaryLinkLoading] = useState(false);
  // 2026-02 — inline nearest-date acknowledgement for the top-row shortcut,
  // so confirming never has to detour through the Report Centre dialog
  // (previously the only place this prompt existed).
  const [nearestAckInfo, setNearestAckInfo] = useState(null);
  const [launchingSummaryTop, setLaunchingSummaryTop] = useState(false);
  const [findingsDialogOpen, setFindingsDialogOpen] = useState(false);
  // Item 9 — Price coverage hard stop dialog state
  const [showPriceCoverageDialog, setShowPriceCoverageDialog] = useState(false);
  const [priceCoverageError, setPriceCoverageError] = useState(null);
  // Release Contract S7 — cost-basis review hard stop dialog state
  const [showCostBasisDialog, setShowCostBasisDialog] = useState(false);
  const [costBasisError, setCostBasisError] = useState(null);
  const [costBasisOverrideReason, setCostBasisOverrideReason] = useState('');
  // Wave 5A P6 — Admin Evidence Drawer (PSB historical sessions only)
  const [adminEvidenceOpen, setAdminEvidenceOpen] = useState(false);

  // Wizard state
  const [wizardStep, setWizardStep] = useState(0);
  const [stepValidation, setStepValidation] = useState({ warnings: [], errors: [] });
  const [draftRestored, setDraftRestored] = useState(false);
  const salesRef = useRef();
  const adjustmentsRef = useRef();
  const deliveriesRef = useRef();
  // iter429 — Flush pending debounced saves on the Opening Stock step
  // before step nav / tab close. Fixes ST011 data loss.
  const openingRef = useRef();

  // Phase 2 — Actual Receipts change handler with debounced save.
  //
  // Online → PUT /sessions/{id}/accounts-sales as before.
  // Offline → persist to IndexedDB local_changes and mutate the in-memory
  // package so a subsequent local recalculate immediately picks up the new
  // incl-VAT figure and the revised methodology headline unblocks.
  const handleAccountsSalesChange = useCallback((next) => {
    setAccountsSales(next);
    if (accountsSalesSaveRef.current) clearTimeout(accountsSalesSaveRef.current);
    accountsSalesSaveRef.current = setTimeout(async () => {
      if (!session?.id) return;
      if (isOfflineMode) {
        try {
          const inclNum = next.accounts_sales_incl_vat === '' || next.accounts_sales_incl_vat == null
            ? null
            : parseFloat(next.accounts_sales_incl_vat);
          const vatRate = parseFloat(offlinePkg?.package?.org_stocktaking_settings?.default_vat_rate ?? 20) || 20;
          const exclNum = inclNum != null && !Number.isNaN(inclNum)
            ? Math.round((inclNum / (1 + vatRate / 100)) * 100) / 100
            : null;
          // Update in-memory package so calculateStockAuditLocally picks it up
          if (offlinePkg?.package?.session) {
            offlinePkg.package.session.accounts_sales_incl_vat = inclNum;
            offlinePkg.package.session.accounts_sales_excl_vat = exclNum;
            offlinePkg.package.session.accounts_sales_notes = next.accounts_sales_notes || null;
          }
          // Keep the derived excl preview in sync with the UI
          if (exclNum != null) {
            setAccountsSales(prev => ({ ...prev, accounts_sales_excl_vat: String(exclNum) }));
          }
          await stocktakeOfflineService.saveLocalChanges(session.id, {
            accounts_sales_incl_vat: inclNum,
            accounts_sales_excl_vat: exclNum,
            accounts_sales_notes: next.accounts_sales_notes || null,
          });
        } catch (e) {
          console.warn('[Wizard] Offline Actual Receipts save failed:', e?.message || e);
        }
        return;
      }
      try {
        const payload = {
          accounts_sales_incl_vat: next.accounts_sales_incl_vat === '' ? null : parseFloat(next.accounts_sales_incl_vat),
          // Phase 4 contract: UI sends only incl-VAT. Backend derives
          // excl-VAT from the org default VAT rate.
          accounts_sales_notes: next.accounts_sales_notes || null,
        };
        const saved = await stocktakeSessionsService.saveAccountsSales(session.id, payload);
        // Keep preview in sync using the server-derived excl-VAT returned
        // by the endpoint (no extra GET required).
        if (saved && saved.accounts_sales_excl_vat != null) {
          setAccountsSales(prev => ({
            ...prev,
            accounts_sales_excl_vat: String(saved.accounts_sales_excl_vat),
          }));
        }
      } catch (e) {
        console.warn('[Wizard] Actual Receipts save failed:', e?.message || e);
      }
    }, 800);
  }, [session?.id, isOfflineMode, offlinePkg]);

  // ── Offline AddFromMaster handler (iter340 v2) ───────────────────────────
  // When the auditor is offline and assigns a product from inventory master,
  // we (1) inject a synthetic "added during count" line into the in-memory
  // session so they can count it immediately, (2) record the assignment
  // in `local_changes.site_inventory_additions` so the sync handler can
  // create the real `SiteBeverageInventory` row + reconcile the count sheet,
  // and (3) tag the synthetic line with the originating `beverage_product_id`
  // so any count_entry the auditor saves can be remapped to the real DB
  // line ID after sync.
  const handleOfflineAssignFromMaster = useCallback(async (product) => {
    if (!product?.id) return;
    // Negative IDs avoid clashing with positive DB-generated PKs and let
    // the sync handler detect "this entry is for a locally-created line".
    const localLineId = -Math.floor(Date.now() + Math.random() * 1000);

    // Pick a default count unit label from the product's unit list
    // (mirrors backend `_get_default_count_unit_label`).
    let unitLabel = null;
    if (Array.isArray(product.units) && product.units.length > 0) {
      const def = product.units.find(u => u.is_default_count_unit) || product.units[0];
      unitLabel = def?.unit_type || null;
    }

    const syntheticLine = {
      id: localLineId,
      session_id: session?.id ?? null,
      beverage_product_id: product.id,
      beverage_location_id: null,
      display_sequence: 99000,
      product_name_snapshot: product.product_name,
      unit_label_snapshot: unitLabel,
      location_name_snapshot: null,
      category_snapshot: product.category || null,
      added_during_count: true,
      count_entry: null,
      _local_added: true,  // local marker — not serialised to backend
    };

    // Mutate the in-memory offline package so reload-from-IndexedDB stays
    // consistent with what the wizard is showing right now.
    if (offlinePkg?.package) {
      offlinePkg.package.count_sheet_lines = [
        ...(offlinePkg.package.count_sheet_lines || []),
        syntheticLine,
      ];
      // Also mark the product as on-site locally so we don't keep offering
      // it in the master picker.
      offlinePkg.package.site_inventory = [
        ...(offlinePkg.package.site_inventory || []),
        {
          id: localLineId,  // synthetic
          site_id: session?.site_id ?? null,
          beverage_product_id: product.id,
          active_for_counting: true,
          default_location_id: null,
          display_sequence: 99000,
        },
      ];
      // Keep productMap fresh so any consumer that resolves product details
      // by id (e.g. yield hint, exception cost flow) finds the new product.
      setProductMap(prev => ({ ...prev, [product.id]: product }));
    }

    // Push the synthetic line into the wizard's lines array so it appears
    // immediately in the Closing-stock review table.
    setLines(prev => [...prev, syntheticLine]);
    // Initialise an empty entry so the row renders editable inputs.
    setEntries(prev => ({
      ...prev,
      [localLineId]: { quantity: '', unit_label: unitLabel || '' },
    }));

    // Persist to local_changes so a hard reload of the wizard restores the
    // synthetic line + queued site_inventory addition.
    try {
      const existing = offlinePkg?.local_changes || {};
      const addedLines = Array.isArray(existing.added_lines) ? [...existing.added_lines] : [];
      addedLines.push(syntheticLine);
      const additions = Array.isArray(existing.site_inventory_additions)
        ? [...existing.site_inventory_additions]
        : [];
      // Idempotent — don't duplicate if already queued for this product.
      if (!additions.some(a => a.beverage_product_id === product.id)) {
        additions.push({ beverage_product_id: product.id });
      }
      await stocktakeOfflineService.saveLocalChanges(Number(id), {
        added_lines: addedLines,
        site_inventory_additions: additions,
      });
      setOfflineSyncState(SYNC_STATES.PENDING);
      // Also reflect on the in-memory record so subsequent saves see it.
      if (offlinePkg) {
        offlinePkg.local_changes = {
          ...(offlinePkg.local_changes || {}),
          added_lines: addedLines,
          site_inventory_additions: additions,
        };
      }
    } catch (e) {
      console.error('[Wizard] Failed to persist offline added_lines', e);
    }
  }, [id, offlinePkg, session?.id, session?.site_id]);

  // iter340 v6 — Offline Site Inventory Setup measure/yield override
  // changes (upsert + delete). Queued to local_changes; synced to backend
  // by the sync handler (which will then PUT/DELETE SiteProductMeasure rows).
  const handleOfflineMeasureChange = useCallback(async (change) => {
    if (!change?.beverage_product_id) return;
    try {
      await stocktakeOfflineService.queueOfflineMeasureChange(Number(id), change);
      // Patch the offline package's site_product_measures so the modal +
      // any consumer that resolves overrides locally stays in sync.
      if (offlinePkg?.package) {
        const pid = Number(change.beverage_product_id);
        const next = (offlinePkg.package.site_product_measures || [])
          .filter(o => Number(o.beverage_product_id) !== pid);
        if (change.action !== 'delete') {
          next.push({
            beverage_product_id: pid,
            measure_ml: change.measure_ml ?? null,
            measure_label: change.measure_label ?? null,
            nominal_servings_per_unit: change.nominal_servings_per_unit ?? null,
          });
        }
        offlinePkg.package.site_product_measures = next;
      }
      setOfflineSyncState(SYNC_STATES.PENDING);
    } catch (e) {
      console.error('[Wizard] Failed to queue offline measure change', e);
    }
  }, [id, offlinePkg]);

  const [calculation, setCalculation] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [costMethod, setCostMethod] = useState('weighted_average');
  const [showCalc, setShowCalc] = useState(false);
  const [validationReport, setValidationReport] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const [observations, setObservations] = useState([]);
  const [observationsLoading, setObservationsLoading] = useState(false);
  const [lightboxSrc, setLightboxSrc] = useState(null);
  const [editingObsId, setEditingObsId] = useState(null);
  const [obsEditForm, setObsEditForm] = useState({});

  // Exception line resolution state
  const [exceptionCosts, setExceptionCosts] = useState({});  // { lineId: costValue }
  const [exceptionCostErrors, setExceptionCostErrors] = useState({}); // { lineId: errorMessage } — keeps per-line save errors visible until retried
  const [resolvingLine, setResolvingLine] = useState(null);
  // iter_fix1_v2 — pre-calculate check result (authoritative exception list)
  const [preCalcData, setPreCalcData] = useState(null);
  const [preCalcLoading, setPreCalcLoading] = useState(false);

  // Auto-save ref
  const autoSaveTimer = useRef(null);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  // iter814 fix — handleEntryChange's auto-resolve-cost step runs after an
  // awaited save inside a setTimeout closure; it needs the LIVE lines list
  // (post-save, with the just-created count_entry), not the stale value
  // captured when the keystroke fired.
  const linesRef = useRef(lines);
  linesRef.current = lines;

  // Load auditors
  useEffect(() => {
    if (!isOfflineMode) {
      auditorsService.list({ is_active: true }).then(data => {
        setAuditors(data || []);
      }).catch(() => {});
    }
  }, [isOfflineMode]);

  // Load observations when on the Closing Stock step
  // Note: We compute the step key inline to avoid temporal dead zone issues with currentStepDef
  useEffect(() => {
    const isStockAuditSession = session?.session_type === 'stock_audit';
    const stepsForSession = isStockAuditSession ? AUDIT_STEPS : VALUATION_STEPS;
    const stepKey = stepsForSession[wizardStep]?.key;
    if (stepKey === 'closing' && id && !isOfflineMode) {
      setObservationsLoading(true);
      api.get(`/count/sessions/${id}/observations`)
        .then(res => setObservations(res.data?.items || []))
        .catch(() => setObservations([]))
        .finally(() => setObservationsLoading(false));
    }
  }, [session?.session_type, wizardStep, id, isOfflineMode]);

  // Observation edit/delete handlers
  const handleDeleteObservation = async (obsId) => {
    if (!window.confirm('Delete this observation?')) return;
    try {
      await api.delete(`/count/observations/${obsId}`);
      setObservations(prev => prev.filter(o => o.id !== obsId));
      toast({ title: 'Observation deleted' });
    } catch {
      toast({ title: 'Failed to delete', variant: 'destructive' });
    }
  };

  const handleStartEditObs = (obs) => {
    setEditingObsId(obs.id);
    setObsEditForm({ note_text: obs.note_text || '', category: obs.category || 'general', severity: obs.severity || 'info' });
  };

  const handleSaveEditObs = async (obsId) => {
    try {
      await api.patch(`/count/observations/${obsId}`, obsEditForm);
      setObservations(prev => prev.map(o => o.id === obsId ? { ...o, ...obsEditForm } : o));
      setEditingObsId(null);
      toast({ title: 'Observation updated' });
    } catch {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  // Check if session is available offline
  useEffect(() => {
    stocktakeOfflineService.isSessionAvailableOffline(Number(id)).then(setIsAvailableOffline);
  }, [id]);

  // Load offline sync state
  useEffect(() => {
    stocktakeOfflineService.getSessionSyncState(Number(id)).then(state => {
      if (state) setOfflineSyncState(state.sync_state);
    });
  }, [id]);

  // Load existing calculation when session loads (online only). Scope C
  // fix: this previously only covered 'valuation' sessions — a 'stock_audit'
  // session's persisted CalculationSnapshot (confirmed present via
  // GET /calculation, which already serializes both types) never
  // rehydrated into `calculation` on page load/revisit, so anything
  // gated on it (the results summary, the Summary shortcut) silently
  // disappeared outside the single visit where Calculate was clicked.
  useEffect(() => {
    if (session?.id && ['valuation', 'stock_audit'].includes(session.session_type) && !isOfflineMode) {
      stocktakeSessionsService.getCalculation(session.id).then(data => {
        if (data?.snapshot) setCalculation(data.snapshot);
      }).catch(() => {});
    }
  }, [session?.id, session?.session_type, isOfflineMode]);

  // Run valuation calculation
  const handleCalculate = async () => {
    if (!session) return;
    setCalculating(true);
    try {
      if (dirty) await handleSave(false);

      // iter439 — multi-location parent cascade. When the auditor clicks
      // Recalculate on the PARENT tab of a multi_with_cellar /
      // multi_no_cellar audit, recalculate every child first so the
      // consolidated review built from per-child snapshots is rebuilt
      // from fresh, current snapshots — not stale ones from before the
      // last edit.
      const isMultiParent =
        !session.parent_session_id &&
        (session.audit_mode === 'multi_with_cellar' ||
         session.audit_mode === 'multi_no_cellar');

      if (isMultiParent) {
        let childIds = [];
        try {
          const list = await api.get(
            `/stocktake/multi-location/sessions/${session.id}/children`,
          );
          childIds = (Array.isArray(list.data) ? list.data : [])
            .map((c) => c.id)
            .filter((cid) => cid && cid !== session.id);
        } catch (childErr) {
          console.warn('[Recalculate] Could not load child list:', childErr);
        }
        if (childIds.length > 0) {
          toast({
            title: 'Recalculating children',
            description: `Refreshing ${childIds.length} location${childIds.length === 1 ? '' : 's'} before consolidating…`,
          });
          for (const cid of childIds) {
            try {
              await stocktakeSessionsService.calculate(cid, costMethod);
            } catch (childCalcErr) {
              console.warn(`[Recalculate] child ${cid} failed:`, childCalcErr);
            }
          }
        }
      }

      const result = await stocktakeSessionsService.calculate(session.id, costMethod);
      setCalculation(result);
      setShowCalc(true);
      toast({ title: 'Calculated', description: `Closing stock value: ${formatCurrency(result.total_value)}` });
    } catch (err) {
      toast({ title: 'Calculation Failed', description: getApiError(err, 'Failed to calculate'), variant: 'destructive' });
    } finally { setCalculating(false); }
  };

  const formatCurrency = (val) => {
    if (val == null) return '\u00A30.00';
    return `\u00A3${Number(val).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  // Load session — online or offline
  const loadSession = useCallback(async () => {
    setLoading(true);
    console.log(`[Wizard] loadSession | isOfflineMode=${isOfflineMode} | networkOnline=${networkOnline} | forceOffline=${forceOffline}`);
    try {
      if (isOfflineMode) {
        // ── Offline: load from IndexedDB ──
        console.log('[Wizard] Source: IndexedDB/Package');
        const record = await stocktakeOfflineService.loadOfflinePackage(Number(id));
        if (!record || !record.package) {
          toast({ title: 'Offline package not found', description: 'Download this session first while online.', variant: 'destructive' });
          navigate('/stocktaking/offline');
          return;
        }
        const pkg = record.package;
        setOfflinePkg(record);
        setOfflineSyncState(record.sync_state);
        // Restore imported observations from local_changes
        if (record.local_changes?.imported_observations?.length > 0) {
          setImportedObservations(record.local_changes.imported_observations);
        }

        // Build session object from package
        // iter340 v2 — also fold in any locally-added "from inventory master"
        // lines so a hard reload of the offline wizard still shows them.
        const lineRecords = [
          ...(pkg.count_sheet_lines || []),
          ...((record.local_changes?.added_lines) || []),
        ];
        const sessionData = {
          ...pkg.session,
          lines: lineRecords.map(line => ({
            ...line,
            count_entry: pkg.count_entries.find(ce => ce.count_sheet_line_id === line.id) || null,
          })),
        };
        setSession(sessionData);

        // Build entries from package (with local changes overlay)
        const localChanges = record.local_changes;
        const localEntries = localChanges?.count_entries || [];
        const initial = {};
        for (const line of lineRecords) {
          // Check local changes first, then package entries
          const localEntry = localEntries.find(e => e.count_sheet_line_id === line.id);
          const pkgEntry = pkg.count_entries.find(ce => ce.count_sheet_line_id === line.id);
          if (localEntry) {
            initial[line.id] = { quantity: localEntry.quantity ?? '', unit_label: localEntry.unit_label || '' };
          } else if (pkgEntry) {
            initial[line.id] = { quantity: pkgEntry.quantity ?? '', unit_label: pkgEntry.unit_label || '' };
          } else {
            initial[line.id] = { quantity: '', unit_label: line.unit_label_snapshot || '' };
          }
        }
        setEntries(initial);
        setLines(lineRecords);

        // Build product map from package
        const map = {};
        (pkg.products || []).forEach(p => { map[p.id] = p; });
        setProductMap(map);

        // Load existing offline calculation
        if (localChanges?.calculation_snapshot) {
          setOfflineCalcResult(localChanges.calculation_snapshot);
        }

        // Hydrate Actual Receipts from package (session header) + local_changes overlay.
        // Phase 4 — offline wizard uses the same AS contract as online so the
        // revised-methodology headline / blocker banner stays in sync.
        const pkgAsIncl = localChanges?.accounts_sales_incl_vat ?? pkg.session?.accounts_sales_incl_vat;
        const pkgAsExcl = localChanges?.accounts_sales_excl_vat ?? pkg.session?.accounts_sales_excl_vat;
        const pkgAsNotes = localChanges?.accounts_sales_notes ?? pkg.session?.accounts_sales_notes;
        setAccountsSales({
          accounts_sales_incl_vat: pkgAsIncl != null && pkgAsIncl !== '' ? String(pkgAsIncl) : '',
          accounts_sales_excl_vat: pkgAsExcl != null && pkgAsExcl !== '' ? String(pkgAsExcl) : '',
          accounts_sales_notes: pkgAsNotes ?? '',
        });
      } else {
        // ── Online: load from API ──
        console.log('[Wizard] Source: Live API');
        const data = await stocktakeSessionsService.get(id);
        setSession(data);
        // Hydrate Actual Receipts (Phase 2)
        setAccountsSales({
          accounts_sales_incl_vat: data.accounts_sales_incl_vat != null ? String(data.accounts_sales_incl_vat) : '',
          accounts_sales_excl_vat: data.accounts_sales_excl_vat != null ? String(data.accounts_sales_excl_vat) : '',
          accounts_sales_notes: data.accounts_sales_notes ?? '',
        });
        const initial = {};
        for (const line of (data.lines || [])) {
          if (line.count_entry) {
            initial[line.id] = {
              quantity: line.count_entry.quantity ?? '',
              unit_label: line.count_entry.unit_label || line.unit_label_snapshot || '',
              // iter593 OCC — store the server's version timestamp so the next
              // save can send it back as client_updated_at for conflict detection.
              // updated_at is null for brand-new entries (first insert); fall
              // back to entered_at so the guard still works after the first save.
              server_updated_at: line.count_entry.updated_at || line.count_entry.entered_at || null,
            };
          } else {
            initial[line.id] = {
              quantity: '',
              unit_label: line.unit_label_snapshot || '',
              server_updated_at: null,  // No server data yet → no OCC check on first insert
            };
          }
        }
        setEntries(initial);
        setLines(data.lines || []);

        const prodIds = [...new Set((data.lines || []).map(l => l.beverage_product_id).filter(Boolean))];
        if (prodIds.length > 0) {
          try {
            const idsParam = prodIds.join(',');
            const resp = await fetch(`${getBackendUrl()}/api/beverage-products?ids=${idsParam}&page_size=${prodIds.length}`, {
              headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` },
            });
            if (resp.ok) {
              const prodData = await resp.json();
              const arr = Array.isArray(prodData) ? prodData : (prodData.items || prodData.products || []);
              const map = {};
              for (const p of arr) { map[p.id] = p; }
              setProductMap(map);
            }
          } catch { /* silent */ }
        }
      }
    } catch (err) {
      // iter362 — Do NOT silently kick the user back to the list on any
      // failure. Production was showing Forest Lodge load → flash → list
      // with no diagnostic. Surface the error and only navigate away when
      // the session genuinely cannot be loaded (404 / 403). Transient
      // errors (network blip, 500 on a related fetch) keep the user on
      // the page so they can retry.
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message || 'Unknown error';
      console.error('[Wizard] loadSession failed', { status, detail, err });
      if (status === 404) {
        toast({ title: 'Session not found', description: 'This stocktake session does not exist or has been deleted.', variant: 'destructive' });
        navigate('/stocktaking/sessions');
      } else if (status === 403) {
        toast({ title: 'No permission', description: 'You do not have access to this session.', variant: 'destructive' });
        navigate('/stocktaking/sessions');
      } else {
        toast({ title: 'Could not load session', description: String(detail).slice(0, 220), variant: 'destructive' });
        // Leave the user on the page; the existing "Loading…" / empty-state
        // copy will show. They can refresh to retry without losing context.
      }
    }
    finally { setLoading(false); }
  }, [id, navigate, isOfflineMode, forceOffline, networkOnline, toast]);

  useEffect(() => { loadSession(); }, [loadSession]);

  // 2026-02 — fetch the Summary link status once a calculation exists,
  // so the single top-row shortcut knows immediately whether to say
  // "Open Summary" (already linked), "Launch/Start Summary" (eligible,
  // not yet created) or hide itself (not eligible at all). Same read
  // the Report Centre dialog already performs — no new backend route.
  useEffect(() => {
    if (!calculation || isOfflineMode || session?.is_psb_historical) {
      setSummaryLinkInfo(null);
      return;
    }
    let cancelled = false;
    setSummaryLinkLoading(true);
    api.get(`/stocktake/sessions/${id}/reports/manifest`)
      .then(res => { if (!cancelled) setSummaryLinkInfo(res.data?.session?.summary_link || null); })
      .catch(() => { if (!cancelled) setSummaryLinkInfo(null); })
      .finally(() => { if (!cancelled) setSummaryLinkLoading(false); });
    return () => { cancelled = true; };
  }, [calculation, isOfflineMode, session?.is_psb_historical, id]);

  // 2026-02 — single-click handler for the top-row Summary shortcut.
  // Linked -> navigate straight to that exact wizard. Eligible/unlinked
  // -> guarded launch (idempotent — safe to click twice), then navigate
  // to the new draft. 2026-02 correction: a nearby-report acknowledgement
  // (409 nearest_date_requires_ack) and a same-moment already_linked race
  // both now resolve inline, directly into the right wizard — neither
  // ever needs a second click through the Report Centre dialog. Only the
  // rare multi-candidate 'ambiguous' conflict still defers to that dialog's
  // dedicated reconciliation UI (not something a single confirm can resolve).
  const launchSummaryAndNavigate = async (acknowledgeNearest = false) => {
    setLaunchingSummaryTop(true);
    try {
      const result = await stocktakeSessionsService.launchSummary(id, acknowledgeNearest);
      setNearestAckInfo(null);
      navigate(`/summary-reports/${result.report.id}`);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 409 && detail?.reason === 'nearest_date_requires_ack') {
        setNearestAckInfo(detail);
      } else if (status === 409 && detail?.reason === 'already_linked' && detail?.report?.id) {
        setNearestAckInfo(null);
        navigate(`/summary-reports/${detail.report.id}`);
      } else if (status === 409 && detail?.reason === 'ambiguous') {
        setNearestAckInfo(null);
        setReportsDialogOpen(true); // genuinely needs the multi-candidate picker
      } else {
        setNearestAckInfo(null);
        toast({
          title: 'Could not open Summary',
          description: (typeof detail === 'string' ? detail : detail?.message) || err.message || 'Unknown error',
          variant: 'destructive',
        });
      }
    } finally {
      setLaunchingSummaryTop(false);
    }
  };

  const handleTopSummaryClick = async () => {
    if (!summaryLinkInfo) return;
    if (summaryLinkInfo.status === 'linked' && summaryLinkInfo.report?.id) {
      navigate(`/summary-reports/${summaryLinkInfo.report.id}`);
      return;
    }
    if (summaryLinkInfo.status === 'eligible_no_report' && !summaryLinkInfo.launch_eligible) {
      navigate(`/jobs/${summaryLinkInfo.job_id}/summary-report?scope=${summaryLinkInfo.scope}`);
      return;
    }
    await launchSummaryAndNavigate(false);
  };

  // Mirror the backend refresh: uncounted lines adopt the site's saved count unit.
  useEffect(() => {
    const onSaved = (ev) => {
      const { sessionId, units } = ev.detail || {};
      if (String(sessionId) !== String(id) || !units) return;
      const isUncounted = (l) => l.count_entry?.quantity == null;
      setLines(prev => prev.map(l => (units[l.beverage_product_id] && isUncounted(l)
        ? { ...l, unit_label_snapshot: units[l.beverage_product_id] } : l)));
      setEntries(prev => {
        const next = { ...prev };
        for (const l of lines) {
          const u = units[l.beverage_product_id];
          const e = next[l.id];
          if (u && e && (e.quantity === '' || e.quantity == null)) next[l.id] = { ...e, unit_label: u };
        }
        return next;
      });
    };
    window.addEventListener('site-count-units-saved', onSaved);
    return () => window.removeEventListener('site-count-units-saved', onSaved);
  }, [id, lines]);


  // Load VAT rate from org settings
  useEffect(() => {
    if (isOfflineMode) {
      // Extract VAT rate from offline package
      const pkgSettings = offlinePkg?.package?.org_stocktaking_settings;
      if (pkgSettings?.default_vat_rate != null) {
        setVatRate(pkgSettings.default_vat_rate);
      }
      return;
    }
    api.get('/stocktake/settings').then(r => {
      setVatRate(r.data?.default_vat_rate ?? 20);
    }).catch(() => setVatRate(20));
  }, [isOfflineMode, offlinePkg]);

  // ── Phase 3: Restore wizard drafts on session load ──
  // iter360b — Resolve the saved position using the step KEY first (resilient
  // to step-list reordering — e.g. inserting Previous YTD between Sales and
  // Calculate would otherwise resurface sessions on the wrong step). Falls
  // back to the legacy integer index, with a smart heuristic for
  // near-completed sessions: if the session was previously calculated,
  // jump to the Calculate step regardless of what was saved.
  useEffect(() => {
    if (!session?.id || isOfflineMode || draftRestored) return;
    // iter360c — Inline the session-type derivations to avoid the TDZ
    // crash that came from referencing `isStockAudit`/`isCellarSession`/
    // `isLocationSession` (declared further down) inside this effect.
    // These three checks must stay in lock-step with the canonical
    // derivations below (lines ~876–880) and the `steps` selector below.
    const sessionType = session?.session_type;
    const auditLocationType = session?.audit_location_type;
    const parentSessionId = session?.parent_session_id;
    const isStockAuditLocal = sessionType === 'stock_audit';
    const isChildLocal = !!parentSessionId;
    const isCellarLocal = isChildLocal && auditLocationType === 'cellar';
    const isLocationLocal = isChildLocal && !isCellarLocal;
    const buildSteps = () => {
      if (!isStockAuditLocal) return VALUATION_STEPS;
      if (isCellarLocal) return CELLAR_AUDIT_STEPS;
      if (isLocationLocal) return LOCATION_AUDIT_STEPS;
      return AUDIT_STEPS;
    };
    const activeSteps = buildSteps();
    stocktakeSessionsService.getWizardDrafts(session.id).then(data => {
      let resolvedIdx = null;
      // 1. Prefer the step KEY when present.
      if (data?.last_wizard_step_key) {
        const idxByKey = activeSteps.findIndex(s => s.key === data.last_wizard_step_key);
        if (idxByKey >= 0) resolvedIdx = idxByKey;
      }
      // 2. Legacy fallback — only trust the saved index when no key is set
      //    AND the index still points at the same kind of step. If the
      //    session has a calculation snapshot (i.e. the auditor reached
      //    Calculate at least once), prefer Calculate over any stale index.
      if (resolvedIdx == null && data?.last_wizard_step != null) {
        const idx = Number(data.last_wizard_step);
        if (Number.isInteger(idx) && idx >= 0 && idx < activeSteps.length) {
          resolvedIdx = idx;
        }
      }
      // 3. Smart default for near-completed sessions: if the session is
      //    already completed/locked OR has any stored calculation snapshot,
      //    default to the Calculate step. This handles Forest Lodge — a
      //    session that was previously parked on `previous_ytd` (after the
      //    YTD step was inserted) but has actually been calculated should
      //    open on Calculate, not on YTD.
      const isNearComplete = ['completed', 'locked'].includes(
        (session?.status || '').toLowerCase()
      ) || data?.has_calculation_snapshot;
      if (isNearComplete) {
        const calcIdx = activeSteps.findIndex(s => s.key === 'calculate');
        if (calcIdx >= 0) resolvedIdx = calcIdx;
      }
      if (resolvedIdx != null) {
        setWizardStep(resolvedIdx);
      }
      setDraftRestored(true);
    }).catch(() => setDraftRestored(true));
  }, [
    session?.id,
    isOfflineMode,
    draftRestored,
    session?.session_type,
    session?.audit_location_type,
    session?.parent_session_id,
    session?.status,
  ]);

  // ── Phase 3: Auto-save wizard draft on step change ──
  const saveDraft = useCallback(async (stepKey, stepIndex, positionStepKey) => {
    if (!session?.id || isOfflineMode) return;
    try {
      const draftPayload = {};
      if (stepKey === 'closing') {
        draftPayload.entries = entriesRef.current;
      }
      await stocktakeSessionsService.saveWizardDraft(session.id, stepKey, draftPayload, stepIndex, positionStepKey);
    } catch { /* silent — draft save is best-effort */ }
  }, [session?.id, isOfflineMode]);

  // Handle entry changes
  const handleEntryChange = (lineId, field, value) => {
    setEntries(prev => ({
      ...prev,
      [lineId]: { ...(prev[lineId] || {}), [field]: value },
    }));
    setDirty(true);
    // Auto-save entries + wizard draft after 2 seconds
    if (autoSaveTimer.current) clearTimeout(autoSaveTimer.current);
    autoSaveTimer.current = setTimeout(async () => {
      await handleSaveLatest(false);
      saveDraft('closing', wizardStep);
      // iter_gram_uat — Added-during-count lines previously always showed a
      // mandatory blank cost input, even when the same session already
      // posted a delivery for this exact product and a valid WAC exists.
      // Once the quantity is persisted, give WAC one silent chance to fill
      // the cost before the operator is asked to type one. A line that
      // already has a cost (manual or a prior auto value) is left alone.
      //
      // iter814 fix — this MUST run only after the quantity save above has
      // resolved (not in parallel): the /resolve and /auto-resolve-cost
      // endpoints both require `line.count_entry` to already exist, and a
      // brand-new added-during-count line has no count_entry row until the
      // bulk save above creates it.
      if (field === 'quantity') {
        const line = linesRef.current.find(l => l.id === lineId);
        const hasCost = line?.count_entry?.cost_price_override !== null
          && line?.count_entry?.cost_price_override !== undefined;
        if (line?.added_during_count && !hasCost && value !== '' && value !== undefined) {
          try {
            const res = await api.post(`/stocktake/sessions/${session.id}/lines/${lineId}/auto-resolve-cost`);
            if (res.data?.resolved) {
              setLines(prev => prev.map(l => l.id !== lineId ? l : {
                ...l,
                count_entry: {
                  ...(l.count_entry || {}),
                  cost_price_override: res.data.cost_price_override,
                  cost_override_source: res.data.cost_override_source,
                  updated_at: res.data.updated_at,
                },
              }));
              // iter814 fix — without refreshing the OCC timestamp the page
              // tracks for this line, the NEXT autosave sends the
              // pre-auto-resolve `updated_at` and the server rejects it as a
              // stale conflict (409) until the page is reloaded.
              setEntries(prev => (prev[lineId] === undefined ? prev : {
                ...prev,
                [lineId]: { ...prev[lineId], server_updated_at: res.data.updated_at },
              }));
            }
          } catch { /* best-effort — falls back to the manual input */ }
        }
      }
    }, 2000);
  };

  // iter364 — Split-count helpers (UI-only). The DB stores ONE
  // `count_entries.quantity` per line; the splits are reduced to a SUM
  // and pushed into entries via handleEntryChange so the existing save
  // flow keeps working unchanged.
  //
  // iter384 — Splits are now UNIT-AWARE. Each split may override its
  // own unit_id from `product.units`. The sum converts every split's
  // qty into the line's target unit using `conversion_to_base` ratios,
  // mirroring the AbbeyZone Count (iter374) behaviour exactly. If a
  // split's chosen unit has no `conversion_to_base` (or is missing
  // entirely), the qty is added at face value (1:1 fallback) and the
  // line is flagged for auditor review.
  const sumSplitsUnitAware = useCallback((splits, prodUnits, targetUnitId) => {
    const units = prodUnits || [];
    const targetUnit = units.find(u => u.id === targetUnitId) || units[0] || null;
    const targetConv = targetUnit ? (parseFloat(targetUnit.conversion_to_base) || 1) : 1;
    return (splits || []).reduce((s, x) => {
      const q = parseFloat(x.qty) || 0;
      if (q === 0) return s;
      const srcUnit = (x.unit_id && units.find(u => u.id === x.unit_id)) || targetUnit;
      const srcConv = srcUnit ? (parseFloat(srcUnit.conversion_to_base) || 1) : 1;
      if (!srcUnit || !targetUnit || srcUnit.id === targetUnit.id) return s + q;
      return s + (q * srcConv / targetConv);
    }, 0);
  }, []);

  // Detect whether any split on a line uses a measure for which the
  // product has no defined `conversion_to_base` — in that case the
  // sum falls back to face-value addition and the auditor should
  // review (per locked Q6.b).
  const splitsNeedReview = useCallback((splits, prodUnits) => {
    if (!splits || splits.length === 0) return false;
    const units = prodUnits || [];
    return splits.some(s => {
      if (!s.unit_id) return false;
      const u = units.find(u => u.id === s.unit_id);
      const conv = u ? parseFloat(u.conversion_to_base) : null;
      return !u || !conv || conv <= 0;
    });
  }, []);

  // Resolve target unit id for a given line (the line's primary unit).
  const resolveTargetUnitId = useCallback((lineId) => {
    const line = lines.find(l => l.id === lineId);
    if (!line) return null;
    const prod = productMap[line.beverage_product_id];
    const prodUnits = prod?.units || [];
    if (prodUnits.length === 0) return null;
    const entry = entries[lineId] || {};
    const label = (entry.unit_label || line.unit_label_snapshot || '').toLowerCase();
    const matched = prodUnits.find(u => (u.unit_type || '').toLowerCase() === label);
    return matched?.id ?? prodUnits[0]?.id ?? null;
  }, [lines, productMap, entries]);

  const sumSplits = useCallback((splits, lineId) => {
    if (!lineId) {
      // Legacy fallback — bare numeric sum (kept for safety; nothing
      // in the wizard should hit this path now that splits carry
      // lineId context).
      return (splits || []).reduce((s, x) => s + (parseFloat(x.qty) || 0), 0);
    }
    const line = lines.find(l => l.id === lineId);
    const prod = line ? productMap[line.beverage_product_id] : null;
    return sumSplitsUnitAware(splits, prod?.units || [], resolveTargetUnitId(lineId));
  }, [lines, productMap, sumSplitsUnitAware, resolveTargetUnitId]);

  const handleSplitChange = (lineId, splitIdx, value) => {
    setEntrySplits(prev => {
      const cur = prev[lineId] || [];
      const next = cur.map((s, i) => (i === splitIdx ? { ...s, qty: value } : s));
      const sum = sumSplits(next, lineId);
      const sumStr = sum > 0 ? String(Math.round(sum * 100) / 100) : '';
      handleEntryChange(lineId, 'quantity', sumStr);
      return { ...prev, [lineId]: next };
    });
  };

  const handleSplitUnitChange = (lineId, splitIdx, unitIdRaw) => {
    setEntrySplits(prev => {
      const cur = prev[lineId] || [];
      const parsed = unitIdRaw ? parseInt(unitIdRaw, 10) : null;
      const next = cur.map((s, i) => (i === splitIdx ? { ...s, unit_id: parsed } : s));
      const sum = sumSplits(next, lineId);
      const sumStr = sum > 0 ? String(Math.round(sum * 100) / 100) : '';
      handleEntryChange(lineId, 'quantity', sumStr);
      return { ...prev, [lineId]: next };
    });
  };

  const handleAddSplit = (lineId) => {
    setEntrySplits(prev => {
      const cur = prev[lineId];
      const targetUnitId = resolveTargetUnitId(lineId);
      if (!cur || cur.length === 0) {
        const currentQty = entries[lineId]?.quantity ?? '';
        return {
          ...prev,
          [lineId]: [
            { qty: currentQty === '' ? '' : String(currentQty), unit_id: targetUnitId, _key: `s0-${Date.now()}` },
            { qty: '', unit_id: targetUnitId, _key: `s1-${Date.now()}` },
          ],
        };
      }
      return {
        ...prev,
        [lineId]: [...cur, { qty: '', unit_id: targetUnitId, _key: `s${cur.length}-${Date.now()}` }],
      };
    });
  };

  const handleRemoveSplit = (lineId, splitIdx) => {
    setEntrySplits(prev => {
      const cur = prev[lineId] || [];
      if (cur.length <= 1) {
        const next = { ...prev };
        delete next[lineId];
        return next;
      }
      const filtered = cur.filter((_, i) => i !== splitIdx);
      if (filtered.length === 1) {
        // Only one split left — promote back to a single quantity. If
        // the survivor used a non-default unit, push that label up too
        // so the line's primary unit reflects the auditor's choice.
        const survivor = filtered[0];
        const line = lines.find(l => l.id === lineId);
        const prod = line ? productMap[line.beverage_product_id] : null;
        const prodUnits = prod?.units || [];
        if (survivor.unit_id) {
          const u = prodUnits.find(pu => pu.id === survivor.unit_id);
          if (u && u.unit_type) {
            handleEntryChange(lineId, 'unit_label', u.unit_type);
          }
        }
        handleEntryChange(lineId, 'quantity', survivor.qty || '');
        const next = { ...prev };
        delete next[lineId];
        return next;
      }
      const sum = sumSplits(filtered, lineId);
      const sumStr = sum > 0 ? String(Math.round(sum * 100) / 100) : '';
      handleEntryChange(lineId, 'quantity', sumStr);
      return { ...prev, [lineId]: filtered };
    });
  };

  // Save entries (using passed entries or state)
  const handleSave = useCallback(async (showToast = true, entriesOverride = null) => {
    if (!session) return;
    const currentEntries = entriesOverride || entriesRef.current;
    setSaving(true);
    try {
      if (isOfflineMode) {
        // ── Offline: save to IndexedDB ──
        // iter340 v2 — for offline-locally-added lines (negative IDs),
        // include the originating beverage_product_id so the sync handler
        // can remap the entry to the real DB line after `_reconcile_count_sheet`.
        const lineById = Object.fromEntries((lines || []).map(l => [l.id, l]));
        const entryList = Object.entries(currentEntries).map(([lineId, entry]) => {
          const numericId = Number(lineId);
          const line = lineById[numericId];
          const out = {
            count_sheet_line_id: numericId,
            quantity: (entry.quantity != null && entry.quantity !== '' && !isNaN(Number(entry.quantity))) ? Number(entry.quantity) : null,
            unit_label: entry.unit_label || null,
            unit_id: null,
          };
          if (numericId < 0 && line?.beverage_product_id) {
            out.beverage_product_id = line.beverage_product_id;
          }
          return out;
        });
        const result = await stocktakeOfflineService.saveLocalChanges(Number(id), { count_entries: entryList });
        if (!result.success) {
          throw new Error(result.error || 'Failed to save to local storage');
        }
        setOfflineSyncState(SYNC_STATES.PENDING);
        setDirty(false);
        if (showToast) toast({ title: 'Saved Locally', description: 'Changes saved to device. Sync when online.' });
      } else {
        // ── Online: save to API ──
        // iter593 OCC — include server_updated_at (the version we last READ) as
        // client_updated_at so the backend can detect if the server has moved on.
        const payload = Object.entries(currentEntries).map(([lineId, entry]) => ({
          line_id: Number(lineId),
          quantity: (entry.quantity != null && entry.quantity !== '' && !isNaN(Number(entry.quantity))) ? Number(entry.quantity) : null,
          unit_label: entry.unit_label || null,
          client_updated_at: entry.server_updated_at || null,
        }));
        const saveResult = await stocktakeSessionsService.saveEntries(session.id, payload);
        setDirty(false);
        setConflictError(null);
        // iter593 OCC — refresh server_updated_at from the response so the next
        // autosave sends an up-to-date version and doesn't spuriously conflict.
        if (saveResult?.session?.lines || saveResult?.lines) {
          const savedLines = saveResult?.session?.lines || saveResult?.lines || [];
          setEntries(prev => {
            const next = { ...prev };
            for (const ln of savedLines) {
              const ce = ln.count_entry;
              if (ce && next[ln.id] !== undefined) {
                next[ln.id] = {
                  ...next[ln.id],
                  server_updated_at: ce.updated_at || ce.entered_at || null,
                };
              }
            }
            return next;
          });
          // Sync server count_entry (qty/unit/cost) so exception checks never use stale values.
          const ceById = new Map(savedLines.filter(ln => ln.count_entry).map(ln => [ln.id, ln.count_entry]));
          setLines(prev => prev.map(l => (ceById.has(l.id) ? { ...l, count_entry: ceById.get(l.id) } : l)));
        }
        if (showToast) toast({ title: 'Saved', description: 'Count entries saved' });
        
        // iter_fix3_v2 — Site-level count unit is auto-persisted by the backend
        // (UPSERT including inherited/no-override rows). The optional inline
        // action here is ONLY for updating the GLOBAL product master default
        // (affects all sites without a site override). No global change without
        // explicit user acceptance.
        const appliedSiteUpdates = saveResult?.applied_site_updates || [];
        for (const s of appliedSiteUpdates) {
          if (!s.to_unit_id) continue;
          toast({
            title: `Site count unit saved: ${s.product_name}`,
            description: `Count unit set to "${s.to_unit}" for this site. Also update the global product master?`,
            duration: 15000,
            action: (
              <button
                className="text-xs font-medium px-2 py-1 bg-primary text-primary-foreground rounded hover:bg-primary/90 ml-2"
                onClick={async () => {
                  try {
                    await stocktakeSessionsService.updateGlobalCountUnit(s.product_id, s.to_unit_id);
                    toast({ title: 'Global master updated', description: `Default count unit for "${s.product_name}" set to "${s.to_unit}" globally` });
                  } catch (e) {
                    toast({ title: 'Could not update global master', description: getApiError(e, 'Update failed'), variant: 'destructive' });
                  }
                }}
              >
                Update Master
              </button>
            ),
          });
        }
      }
    } catch (err) {
      // iter593 OCC — a 409 STALE_CONFLICT means the server has a newer version
      // of one or more lines: show a blocking banner rather than a toast so the
      // user cannot dismiss it without making a deliberate choice (reload vs keep).
      const isStaleConflict =
        err?.response?.status === 409 &&
        err?.response?.data?.detail?.code === 'STALE_CONFLICT';
      if (isStaleConflict) {
        setConflictError(err.response.data.detail);
      } else if (showToast) {
        toast({ title: 'Save Failed', description: getApiError(err, 'Failed'), variant: 'destructive' });
      }
    } finally { setSaving(false); }
  }, [session, toast, isOfflineMode, id, lines]);

  // Auto-save using ref for latest entries (avoids stale closure)
  const handleSaveLatest = useCallback(async (showToast) => {
    return handleSave(showToast, entriesRef.current);
  }, [handleSave]);

  // iter392 — Best-effort save-on-close guard. If the user closes the
  // tab or hits refresh while any wizard step is dirty, fire one final
  // silent save (entries + per-panel refs) and prompt the browser to
  // wait. This is what catches data loss when a deploy lands mid
  // session — the panel-level debounced autosaves (1.5–2 s) usually
  // handle the steady-state, but a sudden close beats their timer.
  useEffect(() => {
    const handler = (e) => {
      let pendingWork = false;
      if (dirty && session?.id) {
        pendingWork = true;
        // Fire-and-forget; modern browsers keep the connection alive
        // briefly when beforeunload preventDefault is set.
        try { handleSaveLatest(false); } catch { /* ignore */ }
      }
      // Panel refs each own their dirty state — ask them to flush.
      [salesRef, deliveriesRef, adjustmentsRef, openingRef].forEach(r => {
        if (r.current?.save) {
          pendingWork = true;
          try { r.current.save(); } catch { /* ignore */ }
        }
      });
      if (pendingWork) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty, session?.id, handleSaveLatest]);

  // iter596b — True save-before-navigate for React Router in-app navigation.
  // Refs mirror current values so the pushState interceptor closure
  // always reads the latest dirty/editable state without stale captures.
  const sessionIdRef = useRef();
  sessionIdRef.current = session?.id;
  const isEditableRef = useRef(false);
  isEditableRef.current = session ? session.status !== 'locked' && !session.is_psb_historical : false;
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  // Replaces iter595's cleanup fire-and-forget which meant navigation could
  // complete before saves were confirmed (user requirement: "do not rely on a
  // background request that may be aborted by navigation").
  //
  // Implementation: intercept window.history.pushState (called by React
  // Router's <Link> and navigate()). Hold navigation until all panel saves
  // resolve, then re-issue via navigate() — which calls pushState again; the
  // `bypassed` flag lets that re-entrant call through directly.
  //
  // Covers: sidebar <Link> clicks, programmatic navigate() calls.
  // Hard-refresh / tab-close: covered separately by the beforeunload handler.
  // Browser back/forward (popstate): best-effort — browsers do not allow
  //   synchronous async blocking of popstate events.
  useEffect(() => {
    if (!session?.id) return undefined;
    const originalPushState = window.history.pushState.bind(window.history);
    let bypassed = false; // True while navigate() calls pushState re-entrantly

    window.history.pushState = (state, title, url) => {
      if (bypassed) return originalPushState(state, title, url);
      if (!isEditableRef.current) return originalPushState(state, title, url);

      const raw = typeof url === 'string' ? url : (url?.href ?? '');
      const isSameOrigin = !raw.startsWith('http') || raw.startsWith(window.location.origin);
      const target = isSameOrigin && raw.startsWith('http')
        ? `${new URL(raw).pathname}${new URL(raw).search}`
        : raw;
      const current = `${window.location.pathname}${window.location.search}`;
      if (!target || target === current) return originalPushState(state, title, url);

      // Flush all panel saves. Navigate ONLY if every save succeeds.
      // iter596c — if any save rejects, abort navigation and show an error toast
      // so the user's data is preserved on-screen (replaces old fire-and-forget
      // .catch(()=>{}) + .finally(navigate) pattern).
      (async () => {
        const results = await Promise.allSettled([
          dirtyRef.current && sessionIdRef.current ? handleSave(false) : Promise.resolve(),
          openingRef.current?.save ? openingRef.current.save() : Promise.resolve(),
          deliveriesRef.current?.save ? deliveriesRef.current.save() : Promise.resolve(),
          adjustmentsRef.current?.save ? adjustmentsRef.current.save() : Promise.resolve(),
          salesRef.current?.save ? salesRef.current.save() : Promise.resolve(),
        ]);
        const anyFailed = results.some(r => r.status === 'rejected');
        if (anyFailed) {
          toast({
            title: 'Could not save changes',
            description: 'One or more sections failed to save. Navigation cancelled — your data is preserved.',
            variant: 'destructive',
          });
          return; // Abort navigation — user stays on the page with data intact
        }
        bypassed = true;
        navigate(target);
        bypassed = false;
      })();
      // Return WITHOUT calling originalPushState — holds navigation until saves done.
    };

    return () => {
      window.history.pushState = originalPushState;
    };
  }, [session?.id, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  // iter_fix1_v2 — Load authoritative exception-line list when the user
  // enters the Calculate & Review step. The pre-calculate-check endpoint
  // applies the zero-qty inactive exemption server-side (no opening stock +
  // no posted deliveries → genuinely inactive line, no cost needed).
  // Reset on step change so stale data never carries forward.
  // Note: We compute the step key inline (like line 497 pattern) to avoid
  // temporal dead zone issues — currentStepDef is declared after this effect.
  useEffect(() => {
    const isStockAuditSession = session?.session_type === 'stock_audit';
    const isChild = !!session?.parent_session_id;
    const stepsForSession = !isStockAuditSession ? VALUATION_STEPS
      : isChild && session?.audit_location_type === 'cellar' ? CELLAR_AUDIT_STEPS
      : isChild ? LOCATION_AUDIT_STEPS : AUDIT_STEPS;
    const stepKey = stepsForSession[wizardStep]?.key;
    if (!['calculate', 'closing'].includes(stepKey) || !session?.id) {
      setPreCalcData(null);
      setPreCalcLoading(false);
      return;
    }
    let cancelled = false;
    setPreCalcLoading(true);
    stocktakeSessionsService.preCalculateCheck(session.id)
      .then(d => { if (!cancelled) setPreCalcData(d); })
      .catch(() => { if (!cancelled) setPreCalcData(null); })
      .finally(() => { if (!cancelled) setPreCalcLoading(false); });
    return () => { cancelled = true; };
  }, [wizardStep, session?.id, session?.session_type, session?.parent_session_id, session?.audit_location_type, lines]); // eslint-disable-line react-hooks/exhaustive-deps
  const handlePublish = async (forcePriceOverride = false, forceCostBasisOverride = false, overrideReason = '') => {
    if (!session) return;
    setPublishing(true);
    try {
      if (dirty) await handleSave(false);
      await stocktakeSessionsService.changeStatus(session.id, 'locked', { forcePriceOverride, forceCostBasisOverride, overrideReason });
      toast({ title: 'Published & Locked', description: 'Session has been locked and published.' });
      await loadSession();
    } catch (err) {
      // Item 9 — Price coverage hard stop: show dialog with missing products
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 422 && detail?.error === 'price_coverage_insufficient') {
        setPriceCoverageError(detail);
        setShowPriceCoverageDialog(true);
      } else if (err?.response?.status === 422 && detail?.error === 'cost_basis_review_required') {
        // Release Contract S7 — cost-basis review hard stop: show dialog
        // requiring an admin override + mandatory reason.
        setCostBasisError(detail);
        setShowCostBasisDialog(true);
      } else {
        toast({ title: 'Publish Failed', description: getApiError(err, 'Failed to publish'), variant: 'destructive' });
      }
    } finally { setPublishing(false); }
  };

  // Pre-deployment defect sweep #1 — `locked` is a TERMINAL state, no
  // unlock path exists anymore (backend rejects the transition).

  // iter360 — Revert a completed session back to In Progress.
  // Common scenario: report was published, then unpublished for edits;
  // session is still marked Completed and the wizard buttons are
  // disabled. Backend already permits this transition.
  const [reverting, setReverting] = useState(false);
  const handleRevertToInProgress = async () => {
    if (!session) return;
    setReverting(true);
    try {
      await stocktakeSessionsService.changeStatus(session.id, 'in_progress');
      toast({ title: 'Reverted to In Progress', description: 'You can now edit the session and re-run the audit.' });
      await loadSession();
    } catch (err) {
      toast({ title: 'Revert Failed', description: getApiError(err, 'Could not revert to In Progress'), variant: 'destructive' });
    } finally { setReverting(false); }
  };
  const canRevertToInProgress = session?.status === 'completed' && isInternalUser && !session?.is_psb_historical;

  // Admin-only unlock: locked → in_progress with confirmation + audit trail (Check 11)
  // Wave 5A P6: PSB historical sessions are permanently read-only — unlock is never permitted.
  const canUnlockSession = session?.status === 'locked' && isAdmin && !session?.is_psb_historical;
  const [showUnlockConfirm, setShowUnlockConfirm] = useState(false);
  const [unlocking, setUnlocking] = useState(false);

  const handleUnlockSession = async () => {
    setUnlocking(true);
    try {
      await stocktakeSessionsService.changeStatus(session.id, 'in_progress');
      toast({ title: 'Session Unlocked', description: 'Session reverted to In Progress. An audit trail entry has been recorded.' });
      await loadSession();
    } catch (e) {
      toast({ title: 'Unlock Failed', description: getApiError(e), variant: 'destructive' });
    } finally {
      setUnlocking(false);
      setShowUnlockConfirm(false);
    }
  };

  // Edit session details
  const startEditing = () => {
    setEditing(true);
    setEditForm({
      session_date: session.session_date,
      period_from: session.period_from || '',
      period_to: session.period_to || '',
      notes: session.notes || '',
      auditor_id: session.auditor_id ? String(session.auditor_id) : '',
    });
  };
  const cancelEditing = () => setEditing(false);
  const saveEditing = async () => {
    try {
      const payload = {
        session_date: editForm.session_date || undefined,
        period_from: editForm.period_from || undefined,
        period_to: editForm.period_to || undefined,
        auditor_id: editForm.auditor_id ? parseInt(editForm.auditor_id, 10) : 0,
        notes: editForm.notes || undefined,
      };
      await stocktakeSessionsService.update(session.id, payload);
      setEditing(false);
      await loadSession();
      toast({ title: 'Session Updated' });
    } catch (err) {
      toast({ title: 'Update Failed', description: getApiError(err, 'Failed'), variant: 'destructive' });
    }
  };

  // Derived data
  const isStockAudit = session?.session_type === 'stock_audit';
  const isMultiLocation = session?.audit_mode && session.audit_mode !== 'single' && !session.parent_session_id;
  const isChildSession = !!session?.parent_session_id;
  const isCellarSession = isChildSession && session?.audit_location_type === 'cellar';
  const isLocationSession = isChildSession && !isCellarSession;

  // Pick the right wizard steps
  const steps = (() => {
    if (!isStockAudit) return VALUATION_STEPS;
    if (isCellarSession) return CELLAR_AUDIT_STEPS;
    if (isLocationSession) {
      const hasCellar = session?.audit_mode === 'multi_with_cellar';
      return LOCATION_AUDIT_STEPS.map(s => (s.key !== 'transfers' ? s : {
        ...s,
        label: 'Transfers between locations',
        description: hasCellar
          ? 'Stock received from or returned to the cellar, and moved between locations'
          : 'Stock moved in from and out to other locations',
      }));
    }
    return AUDIT_STEPS;
  })();
  // A restored step index from a different wizard shape must not show "Step 6 of 5".
  useEffect(() => {
    if (wizardStep > steps.length - 1) setWizardStep(0);
  }, [wizardStep, steps.length]);
  const currentStepDef = steps[wizardStep] || steps[0];
  const isEditable = session && session.status !== 'locked' && !session.is_psb_historical;
  const countedLines = lines.filter(l => entries[l.id]?.quantity !== undefined && entries[l.id]?.quantity !== '').length;

  // Closing stock: available categories/subcategories
  // iter360b — Resolve the subcategory using a UNION of master + snapshot.
  // Previously we only read from `productMap[l.beverage_product_id]?.subcategory`,
  // so any product whose master row got soft-deleted / renamed / not
  // returned by the products fetch silently lost its subcategory in the
  // wizard's filter list (e.g. Post-Mix syrups went missing). The
  // `subcategory_snapshot` is captured at session creation and survives
  // master-data churn.
  const resolveLineSubcat = (l) => {
    const p = productMap[l.beverage_product_id];
    return (p?.subcategory || l.subcategory_snapshot) || null;
  };
  const closingAvailableCategories = [...new Set(lines.map(l => l.category_snapshot).filter(Boolean))].sort();
  const closingAvailableSubcategories = [...new Set(lines.map(resolveLineSubcat).filter(Boolean))].sort();
  const closingActiveFilterCount = closingSelectedCategories.length + closingSelectedSubcategories.length;

  // Filter closing stock lines by search + category + subcategory
  const filteredClosingLines = lines.filter(l => {
    if (closingSelectedCategories.length > 0) {
      const cat = l.category_snapshot || '';
      if (!closingSelectedCategories.includes(cat)) return false;
    }
    if (closingSelectedSubcategories.length > 0) {
      const sub = resolveLineSubcat(l);
      if (!sub || !closingSelectedSubcategories.includes(sub)) return false;
    }
    if (closingSearch) {
      const q = closingSearch.toLowerCase();
      const name = (l.product_name_snapshot || '').toLowerCase();
      const code = (productMap[l.beverage_product_id]?.product_code || '').toLowerCase();
      if (!name.includes(q) && !code.includes(q)) return false;
    }
    return true;
  });

  // Group filtered lines by SUBCATEGORY using the canonical order defined in
  // subcategoryOrder.js; "Other" pinned last for any line missing a subcategory.
  const groupedLines = (() => {
    const map = new Map();
    for (const l of filteredClosingLines) {
      const sub = (resolveLineSubcat(l) && String(resolveLineSubcat(l)).trim()) || 'Other';
      if (!map.has(sub)) map.set(sub, []);
      map.get(sub).push(l);
    }
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.product_name_snapshot || '').localeCompare(b.product_name_snapshot || ''));
    }
    const keys = [...map.keys()].sort(subcategoryComparator);
    const ordered = {};
    for (const k of keys) ordered[k] = map.get(k);
    return ordered;
  })();

  // Exception lines: added during count, counted, needing cost resolution.
  //
  // Bug fix (2026-04-20) — Michael-vs-Ammar cost-override sync issue:
  // previously we treated EITHER a server value OR a Michael-typed local
  // value as "resolved". That let a silently-failed PUT leave Michael
  // looking green while Ammar saw amber, because Ammar's browser has no
  // local-state fallback. The authoritative source is the server
  // (`count_entry.cost_price_override`). Local state is only used as an
  // in-flight preview while the debounced PUT is saving.
  //
  // Failed-save tracking (`exceptionCostErrors`) now keeps a line red
  // with a visible error, so operators never see a false "resolved".
  const exceptionLines = lines.filter(l => {
    if (!l.added_during_count) return false;
    const e = entries[l.id];
    if (!e || e.quantity === undefined || e.quantity === '') return false;
    const serverCost = l.count_entry?.cost_price_override;
    // >= 0 check: an explicitly-set £0 (FOC) is a valid, resolved cost.
    // Previously `serverCost && serverCost > 0` failed for zero because
    // JS treats 0 as falsy, making FOC lines permanently amber.
    if (serverCost !== null && serverCost !== undefined && serverCost >= 0) return false;
    return true; // Unresolved — server has no value, show the input
  });
  const resolvedExceptionLines = lines.filter(l => {
    if (!l.added_during_count) return false;
    const e = entries[l.id];
    if (!e || e.quantity === undefined || e.quantity === '') return false;
    const serverCost = l.count_entry?.cost_price_override;
    return serverCost !== null && serverCost !== undefined && serverCost >= 0;
  });
  // iter_pt10 — Also surface non-ADC inventory lines that have a nonzero
  // closing count but no resolvable cost (flagged by the backend with
  // is_adc=false in unresolved_exceptions).
  const nonAdcBlockedIds = new Set(
    (preCalcData?.unresolved_exceptions || [])
      .filter(e => e.is_adc === false)
      .map(e => e.line_id)
  );
  // cost_source map: line_id → 'missing' | other — used to label badge.
  const exceptionCostSourceMap = new Map(
    (preCalcData?.unresolved_exceptions || []).map(e => [e.line_id, e.cost_source])
  );
  const allExceptionLines = lines.filter(l => l.added_during_count || nonAdcBlockedIds.has(l.id));
  const isFirstSession = preCalcData?.is_first_session ?? false;

  // iter_fix1_v2 — Derive authoritative "blocks calculation" list.
  // When preCalcData is available (loaded on calculate step entry), use the
  // backend's unresolved_exceptions list which applies the zero-qty inactive
  // exemption. A line with qty=0 and no opening stock / deliveries is NOT
  // in that list even though it has no cost_price_override.
  // Falls back to the client-side `exceptionLines` filter until the API responds.
  const preCalcUnresolvedIds = new Set(
    (preCalcData?.unresolved_exceptions || []).map(e => e.line_id)
  );
  // Server cleared the line AND the on-screen qty still matches what the server judged.
  // iter815 fix — the pre-calc-check refetch is triggered by `lines` changing
  // (a just-saved count), but the OLD (stale) preCalcData stays in state
  // until that refetch resolves (can take >9s on this preview's DB). During
  // that window a freshly-counted, still-uncosted ADC line was wrongly
  // shown as exempt/"Cost available" because it simply wasn't present yet
  // in the stale unresolved_exceptions list. While a refetch is in flight,
  // never trust the stale snapshot — fall back to treating the line as not
  // yet confirmed exempt (the raw client-side check in `exceptionLines`
  // still applies and shows a "Checking cost…" state instead of a false
  // "Resolved").
  const isServerExempt = (l) => !preCalcLoading && !!preCalcData
    && !preCalcUnresolvedIds.has(l.id)
    && l.count_entry?.quantity != null
    && Number(entries[l.id]?.quantity) === Number(l.count_entry.quantity);
  const unresolvedExceptionLines = exceptionLines.filter(l => !isServerExempt(l));
  // Non-ADC lines from preCalcData that are still unresolved (no cost_price_override set).
  // These are existing inventory lines with a nonzero count but no resolvable cost,
  // flagged by the backend with is_adc=false in unresolved_exceptions.
  const unresolvedNonAdcLines = lines.filter(l => {
    if (!nonAdcBlockedIds.has(l.id)) return false;
    const serverCost = l.count_entry?.cost_price_override;
    return serverCost === null || serverCost === undefined || serverCost < 0;
  });
  // Combined blocker list: ADC unresolved + non-ADC unresolved.
  // Drives panel state and Calculate-step blocking banner so both line
  // types are counted (badge, border colour, blocker) consistently.
  const allUnresolvedExceptionLines = [...unresolvedExceptionLines, ...unresolvedNonAdcLines];

  // Handler: auto-resolve exception line cost (debounced).
  //
  // Bug fix (2026-04-20):
  // • On success → clear the local `exceptionCosts[lineId]` entry so the
  //   server value becomes the single source of truth. This also makes
  //   the "green/resolved" badge appear consistently for every user, not
  //   just the one who typed it.
  // • On failure → record the error in `exceptionCostErrors[lineId]` so
  //   the input stays red with a visible message until retried; the
  //   operator never sees a false "resolved" state.
  // • Toast messages surface errors loudly instead of only on the first
  //   attempt so silent failures stop sneaking through.
  // 2026-05-08 (iter340 — Pim's No 1 Cup bug fix):
  // OLD: 800ms debounce auto-saved while the user was still typing.
  //      Symptoms: "system attempts to save/process whilst user is
  //      inputting" and partial saves that wiped subsequent keystrokes.
  // NEW: capture only — no auto-save here. The save fires on input
  //      blur (or Enter), via `commitExceptionCost`.
  const resolveTimers = useRef({});  // legacy ref retained for cleanup safety
  const handleExceptionCostChange = useCallback((lineId, costValue) => {
    setExceptionCosts(prev => ({ ...prev, [lineId]: costValue }));
    setExceptionCostErrors(prev => {
      if (!prev[lineId]) return prev;
      const next = { ...prev };
      delete next[lineId];
      return next;
    });
  }, []);

  const commitExceptionCost = useCallback(async (lineId, costValueArg, unitLabel) => {
    // 2026-05-08 (iter340 — Pim's persistence bug): always read the cost
    // from the input directly. Reading from `exceptionCosts[lineId]`
    // suffered a stale-closure race: on rapid type→blur, React hadn't
    // committed the onChange setState yet, so `commitExceptionCost`
    // observed the PREVIOUS (often empty) value, hit the early-return,
    // and silently dropped the save. The auditor saw the input go blank
    // on reload because nothing was ever PUT.
    let costValue = costValueArg;
    if (costValue === undefined || costValue === null) {
      costValue = exceptionCosts[lineId];
    }
    const costStr = costValue == null ? '' : String(costValue).trim();
    // Allow 0 for FOC (Free of Charge) lines. Only reject empty, NaN, or negative.
    const parsed = parseFloat(costStr);
    if (costStr === '' || isNaN(parsed) || parsed < 0) return;
    if (resolveTimers.current[lineId]) clearTimeout(resolveTimers.current[lineId]);
    setResolvingLine(lineId);
    try {
      await api.put(`/stocktake/sessions/${session.id}/lines/${lineId}/resolve`, {
        cost_price_override: parsed,
        unit_label: unitLabel || undefined,
      });
      // Targeted state update — do NOT call loadSession() here.
      // loadSession() triggers setLoading(true) which hits the early-return
      // spinner at render time, unmounting the wizard and losing scroll
      // position. Instead, update only the affected line's count_entry in
      // local state so the exception panel updates immediately and the user
      // can continue entering the next cost without scrolling back down.
      setLines(prev => prev.map(l => {
        if (l.id !== lineId) return l;
        return {
          ...l,
          count_entry: {
            ...(l.count_entry || {}),
            cost_price_override: parsed,
            cost_override_source: 'user',
          },
        };
      }));
      setExceptionCosts(prev => {
        if (!(lineId in prev)) return prev;
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
    } catch (err) {
      const msg = getApiError(err, 'Error');
      setExceptionCostErrors(prev => ({ ...prev, [lineId]: msg }));
      toast({ title: 'Failed to save cost', description: msg, variant: 'destructive' });
    } finally {
      setResolvingLine(null);
    }
  }, [exceptionCosts, session?.id, toast]);

  // iter379/iter380 — "Refresh Data from Master" handler. Calls the
  // heal-snapshots endpoint to repull live product names, default
  // unit labels, missing count-entry costs AND back-fill missing
  // transfer costs (cellar → bar inter-location movements), then
  // reloads the session so the wizard renders fresh data.
  const handleHealSnapshots = useCallback(async () => {
    if (!session?.id || healing) return;
    setHealing(true);
    try {
      const result = await stocktakeSessionsService.healSessionSnapshots(session.id);
      const parts = [];
      if (result.names_updated) parts.push(`${result.names_updated} name${result.names_updated !== 1 ? 's' : ''}`);
      if (result.units_updated) parts.push(`${result.units_updated} unit${result.units_updated !== 1 ? 's' : ''}`);
      if (result.entry_units_filled) parts.push(`${result.entry_units_filled} entry unit${result.entry_units_filled !== 1 ? 's' : ''}`);
      if (result.costs_filled) parts.push(`${result.costs_filled} cost${result.costs_filled !== 1 ? 's' : ''}`);
      if (result.categories_updated) parts.push(`${result.categories_updated} categor${result.categories_updated !== 1 ? 'ies' : 'y'}`);
      if (result.transfer_lines_healed) parts.push(`${result.transfer_lines_healed} transfer cost${result.transfer_lines_healed !== 1 ? 's' : ''}`);
      const desc = parts.length
        ? `Refreshed ${parts.join(', ')} across ${result.lines_scanned} line${result.lines_scanned !== 1 ? 's' : ''}.`
        : `Already up-to-date (${result.lines_scanned} line${result.lines_scanned !== 1 ? 's' : ''} scanned).`;
      toast({ title: 'Master data refreshed', description: desc });
      // Also refresh the master productMap so the unit dropdowns repopulate.
      await loadSession();
    } catch (err) {
      const msg = getApiError(err, 'Failed to refresh from master');
      toast({ title: 'Refresh failed', description: msg, variant: 'destructive' });
    } finally {
      setHealing(false);
    }
  }, [session?.id, healing, toast, loadSession]);

  // Step validation — runs on every step, shows warnings/errors inline
  const validateCurrentStep = () => {
    const warnings = [];
    const errors = [];

    if (currentStepDef.key === 'opening') {
      if (!session?.period_from || !session?.period_to) {
        errors.push('Session period dates (From / To) must be set before proceeding.');
      }
      if (session?.period_from && session?.period_to && session.period_from > session.period_to) {
        errors.push('Period "From" date is after "To" date. Please correct the dates.');
      }
    } else if (currentStepDef.key === 'deliveries') {
      // No hard errors — just informational
    } else if (currentStepDef.key === 'sales') {
      // No hard errors — just informational
    } else if (currentStepDef.key === 'adjustments') {
      // No hard errors — just informational
    } else if (currentStepDef.key === 'closing') {
      if (countedLines === 0) {
        warnings.push('No closing count entries have been entered yet.');
      } else if (countedLines < lines.length) {
        warnings.push(`${countedLines}/${lines.length} products counted (${Math.round(countedLines / lines.length * 100)}%). Uncounted products will be treated as zero.`);
      }
      // Check for entries with unit selected but no quantity
      const unitNoQty = lines.filter(l => {
        const e = entries[l.id] || {};
        return e.unit_label && (!e.quantity || e.quantity === '');
      });
      if (unitNoQty.length > 0) {
        warnings.push(`${unitNoQty.length} product(s) have a unit selected but no quantity entered.`);
      }
    } else if (currentStepDef.key === 'calculate') {
      if (countedLines === 0) {
        errors.push('Cannot calculate without any closing count entries. Go back to the Closing Stock step.');
      }
    }
    setStepValidation({ warnings, errors });
    return errors.length === 0;
  };

  const handleNextStep = async () => {
    // Auto-save current step data
    if (dirty) await handleSave(false);
    if (currentStepDef.key === 'opening' && openingRef.current) {
      await openingRef.current.save();
    }
    if (currentStepDef.key === 'deliveries' && deliveriesRef.current) {
      await deliveriesRef.current.save();
    }
    if (currentStepDef.key === 'sales' && salesRef.current) {
      await salesRef.current.save();
    }
    if (currentStepDef.key === 'adjustments' && adjustmentsRef.current) {
      await adjustmentsRef.current.save();
    }
    // Persist observations when leaving closing stock step
    if (currentStepDef.key === 'closing' && importedObservations.length > 0 && isOfflineMode && offlinePkg) {
      await stocktakeOfflineService.saveLocalChanges(Number(id), { imported_observations: importedObservations });
    }
    if (!validateCurrentStep()) return;
    if (wizardStep < steps.length - 1) {
      const nextStep = wizardStep + 1;
      // Persist draft and wizard position
      // iter360b — pass the DESTINATION step key so the wizard restores
      // there next time, not on the step we're leaving.
      saveDraft(currentStepDef.key, nextStep, steps[nextStep]?.key);
      setWizardStep(nextStep);
      setStepValidation({ warnings: [], errors: [] });
    }
  };
  const handlePrevStep = async () => {
    if (wizardStep > 0) {
      // Auto-save current step data before going back
      if (dirty) await handleSave(false);
      if (currentStepDef.key === 'opening' && openingRef.current) {
        await openingRef.current.save();
      }
      if (currentStepDef.key === 'deliveries' && deliveriesRef.current) {
        await deliveriesRef.current.save();
      }
      if (currentStepDef.key === 'sales' && salesRef.current) {
        await salesRef.current.save();
      }
      if (currentStepDef.key === 'adjustments' && adjustmentsRef.current) {
        await adjustmentsRef.current.save();
      }
      const prevStep = wizardStep - 1;
      saveDraft(currentStepDef.key, prevStep, steps[prevStep]?.key);
      setWizardStep(prevStep);
      setStepValidation({ warnings: [], errors: [] });
    }
  };
  const handleStepClick = async (idx) => {
    // Auto-save current step data before jumping
    if (dirty) await handleSave(false);
    if (currentStepDef.key === 'opening' && openingRef.current) {
      await openingRef.current.save();
    }
    if (currentStepDef.key === 'deliveries' && deliveriesRef.current) {
      await deliveriesRef.current.save();
    }
    if (currentStepDef.key === 'sales' && salesRef.current) {
      await salesRef.current.save();
    }
    if (currentStepDef.key === 'adjustments' && adjustmentsRef.current) {
      await adjustmentsRef.current.save();
    }
    saveDraft(currentStepDef?.key || 'opening', idx, steps[idx]?.key);
    setWizardStep(idx);
    setStepValidation({ warnings: [], errors: [] });
  };

  // ── Offline: Download session package ──
  const handleDownloadForOffline = async () => {
    setDownloading(true);
    try {
      const result = await stocktakeOfflineService.downloadSessionPackage(Number(id));
      if (result.success) {
        setIsAvailableOffline(true);
        setOfflineSyncState(SYNC_STATES.SYNCED);
        toast({ title: 'Downloaded for Offline', description: 'Session package saved to device.' });
        refreshOfflineSessions();
      } else {
        toast({ title: 'Download Failed', description: result.error, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Download Failed', description: err.message, variant: 'destructive' });
    } finally { setDownloading(false); }
  };

  // ── Offline: Sync session back to server ──
  const handleSyncToServer = async () => {
    setSyncing(true);
    try {
      const result = await syncStocktakeSession(Number(id));
      if (result.sync_status === 'synced') {
        setOfflineSyncState(SYNC_STATES.SYNCED);
        toast({ title: 'Synced Successfully', description: 'All offline changes applied to the server.' });
        // Reload from server to get fresh state
        loadSession();
      } else if (result.sync_status === 'conflict') {
        setOfflineSyncState(SYNC_STATES.CONFLICT);
        const reason = result.conflict_details?.reason || 'Session was modified on the server since your last download.';
        toast({ title: 'Sync Conflict', description: reason, variant: 'destructive' });
      }
    } catch (err) {
      toast({ title: 'Sync Failed', description: err.message, variant: 'destructive' });
    } finally { setSyncing(false); }
  };

  // ── Import count data from tablet file (handles both count lines and observations) ──
  // iter427 (ST0014) — When a tablet exports counts for products that
  // (a) don't exist on the current count sheet, or (b) don't match a
  // beverage_product the auditor can see, the import used to silently
  // drop them. The user only noticed when Sales detected a shortage.
  // New behaviour:
  //   1. Track EVERY unmatched counted_line with reason (no_csl /
  //      product_missing / no_product_id).
  //   2. For unmatched lines where the beverage_product_id IS valid,
  //      append a new count_sheet_line on the parent (the next
  //      reconcile will copy it to multi-loc children too).
  //   3. Toast surfaces: matched / created / failed-by-reason, plus a
  //      modal-style block listing the affected products.
  const handleImportCountData = async (jsonText) => {
    const data = JSON.parse(jsonText);
    if (data.transfer_type !== 'count_data' || !data.counted_lines) {
      toast({ title: 'Invalid file', description: 'This does not appear to be an AbbeyZone count export file.', variant: 'destructive' });
      return null;
    }
    // 1. Match against existing count sheet lines
    let matched = 0;
    let toCreate = [];          // counted_lines where pid is valid but no csl
    let unmatched = [];         // truly orphan (no pid match at all)
    const updatedEntries = { ...entries };
    for (const cl of data.counted_lines) {
      if (cl.quantity == null) continue;  // skip blank rows
      let line = lines.find(l => l.id === cl.count_sheet_line_id);
      if (!line) line = lines.find(l => l.beverage_product_id === cl.beverage_product_id);
      if (line) {
        updatedEntries[line.id] = {
          ...updatedEntries[line.id],
          quantity: String(cl.quantity),
          unit_id: cl.unit_id || updatedEntries[line.id]?.unit_id || '',
          unit_label: cl.unit_label || updatedEntries[line.id]?.unit_label || '',
          notes: cl.notes || updatedEntries[line.id]?.notes || '',
        };
        matched++;
      } else if (cl.beverage_product_id) {
        toCreate.push(cl);
      } else {
        unmatched.push(cl);
      }
    }
    setEntries(updatedEntries);

    // 2. Create new count sheet lines for products that were counted
    //    on the tablet but are NOT on the current count sheet.
    //    Server validates the beverage_product_id belongs to the org
    //    and refuses anything else (returned in `rejected`).
    let createdCount = 0;
    const serverRejected = [];
    if (toCreate.length > 0 && session?.id && !isOfflineMode) {
      try {
        const payload = {
          products: toCreate.map(cl => ({
            beverage_product_id: cl.beverage_product_id,
            count_quantity: cl.quantity,
            unit_id: cl.unit_id || null,
            unit_label: cl.unit_label || null,
            notes: cl.notes || null,
          })),
        };
        const res = await api.post(`/stocktake/sessions/${session.id}/count-sheet/append-counted`, payload);
        createdCount = res.data?.created || 0;
        (res.data?.rejected || []).forEach(r => serverRejected.push(r));
        // Reload session so new lines + counts appear
        if (createdCount > 0) {
          await loadSession();
        }
      } catch (err) {
        console.warn('append-counted failed:', err);
        // Fall back: surface them as unmatched in the warning toast
        toCreate.forEach(cl => unmatched.push({ ...cl, _reason: 'append_failed' }));
      }
    } else {
      // Offline — can't create server-side, surface as unmatched
      toCreate.forEach(cl => unmatched.push({ ...cl, _reason: 'offline_skip_create' }));
    }

    // 3. Import observations (with photos)
    const obs = data.observations || [];
    if (obs.length > 0) {
      setImportedObservations(obs);
      // If online, sync observations to server immediately
      if (!isOfflineMode && session?.id) {
        try {
          const obsPayload = {
            observations: obs.map(o => ({
              local_id: o.local_id,
              note_text: o.note_text,
              category: o.category,
              severity: o.severity,
              product_id: o.product_id,
              product_name_snapshot: o.product_name_snapshot,
              location_id: o.location_id,
              location_name_snapshot: o.location_name_snapshot,
              created_at: o.created_at,
            })),
          };
          const syncRes = await api.post(`/count/sessions/${session.id}/observations/sync`, obsPayload);
          const idMap = syncRes.data?.id_map || {};
          // Upload photos for each observation
          let photosUploaded = 0;
          for (const o of obs) {
            const serverId = idMap[o.local_id];
            if (!serverId) continue;
            for (const photo of (o.photos || [])) {
              if (!photo.data_base64) continue;
              try {
                const binaryStr = atob(photo.data_base64);
                const bytes = new Uint8Array(binaryStr.length);
                for (let i = 0; i < binaryStr.length; i++) bytes[i] = binaryStr.charCodeAt(i);
                const blob = new Blob([bytes], { type: photo.mime_type || 'image/jpeg' });
                const formData = new FormData();
                formData.append('file', blob, photo.file_name || 'photo.jpg');
                if (photo.caption) formData.append('caption', photo.caption);
                await api.post(`/count/observations/${serverId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                photosUploaded++;
              } catch { /* photo upload failed, non-blocking */ }
            }
          }
          toast({ title: `${obs.length} observations synced${photosUploaded > 0 ? `, ${photosUploaded} photos uploaded` : ''}` });
        } catch (err) {
          console.warn('Observation sync failed (saved locally):', err);
        }
      }
    }

    // 4. Build a result string. The unmatched / rejected breakdown is
    //    front-and-centre so the auditor never silently loses a count
    //    again (ST0014 Peroni / Diet Coke bottle / Rekorderlig bug).
    const parts = [`${matched} of ${data.counted_lines.length} count lines matched`];
    if (createdCount) parts.push(`${createdCount} new product${createdCount === 1 ? '' : 's'} added to the sheet`);
    if (obs.length > 0) {
      const photoCount = obs.reduce((s, o) => s + (o.photos || []).length, 0);
      parts.push(`${obs.length} observations${photoCount > 0 ? ` with ${photoCount} photos` : ''}`);
    }
    const allRejected = [...unmatched, ...serverRejected];
    if (allRejected.length > 0) {
      const sample = allRejected.slice(0, 5)
        .map(r => r.product_name_snapshot || r.product_name || `product ${r.beverage_product_id ?? '?'}`)
        .join(', ');
      const more = allRejected.length > 5 ? ` and ${allRejected.length - 5} more` : '';
      // Loud warning toast — destructive variant so the auditor sees it
      toast({
        title: `${allRejected.length} count${allRejected.length === 1 ? '' : 's'} could NOT be imported`,
        description: `Affected: ${sample}${more}. These products don't exist in your inventory master — add them first, then re-import. Common fixes: (a) the tablet was counting an ad-hoc product that was never created in the master, or (b) the product_id on the tablet doesn't match this org.`,
        variant: 'destructive',
        duration: 15000,
      });
    }
    return {
      title: `Import Complete`,
      description: parts.join(' + ') + (allRejected.length > 0 ? ` · ${allRejected.length} REJECTED — see warning toast` : ''),
    };
  };

  // ── Offline: Local calculation ──
  const handleLocalCalculate = async () => {
    if (!offlinePkg?.package) {
      console.warn('[Calculate] BLOCKED: offlinePkg not available');
      return;
    }
    setCalculating(true);
    try {
      if (dirty) await handleSave(false);
      // Re-read local changes
      const record = await stocktakeOfflineService.loadOfflinePackage(Number(id));
      const localEntries = record?.local_changes?.count_entries || 
        Object.entries(entriesRef.current).map(([lineId, entry]) => ({
          count_sheet_line_id: Number(lineId),
          quantity: entry.quantity != null && entry.quantity !== '' ? Number(entry.quantity) : null,
          unit_id: null,
          unit_label: entry.unit_label || null,
        }));
      const localSales = record?.local_changes?.sales_entries || record?.package?.sales_entries || [];
      const localAdj = record?.local_changes?.adjustment_entries || record?.package?.adjustment_entries || [];

      // Merge offline-added deliveries/credits into the package for calculation
      const mergedPkg = { ...record.package };
      const offlineDels = record?.local_changes?.offline_deliveries || [];
      const offlineCreds = record?.local_changes?.offline_credits || [];
      if (offlineDels.length > 0) {
        mergedPkg.deliveries = [...(mergedPkg.deliveries || []), ...offlineDels.map(d => ({
          id: d.id,
          delivery_date: d.delivery_date,
          status: 'posted',
          lines: (d.lines || []).map(l => ({
            beverage_product_id: l.beverage_product_id,
            quantity: l.quantity,
            stock_quantity: l.quantity,
            cost_price: l.cost_price,
            line_total: l.line_total || (l.quantity * l.cost_price),
            unit_label: l.unit_label,
          })),
        }))];
      }
      if (offlineCreds.length > 0) {
        mergedPkg.credits = [...(mergedPkg.credits || []), ...offlineCreds.map(c => ({
          id: c.id,
          credit_date: c.delivery_date || c.credit_date,
          status: 'posted',
          lines: (c.lines || []).map(l => ({
            beverage_product_id: l.beverage_product_id,
            quantity: l.quantity,
            stock_quantity: l.quantity,
            cost_price: l.cost_price,
            line_total: l.line_total || (l.quantity * l.cost_price),
            unit_label: l.unit_label,
          })),
        }))];
      }

      console.log('[Calculate] Source: LOCAL ENGINE | entries:', localEntries.length, '| sales:', localSales.length, '| adj:', localAdj.length);
      console.log('[Calculate] Package opening_stock:', mergedPkg.opening_stock?.length, '| deliveries:', mergedPkg.deliveries?.length, '(+', offlineDels.length, 'offline)');

      // Run pre-calculation validation
      const preValidation = generateVerificationNotes(mergedPkg, localEntries, localSales, localAdj, null);
      if (preValidation.errors.length > 0) {
        console.warn('[Calculate] PRE-CALC VALIDATION FAILED:', preValidation.errors);
        setStepValidation({ errors: preValidation.errors.map(e => e.message), warnings: preValidation.warnings.map(w => w.message) });
        setValidationReport(preValidation);
        toast({ title: 'Validation Failed', description: preValidation.errors[0].message, variant: 'destructive' });
        setCalculating(false);
        return;
      }

      const result = calculateStockAuditLocally(mergedPkg, localEntries, localSales, localAdj);
      console.log('[Calculate] RESULT:', { opening: result.opening_stock_value, deliveries: result.deliveries_value, expected: result.expected_closing_value, actual: result.total_value });
      setOfflineCalcResult(result);
      await stocktakeOfflineService.saveLocalCalculation(Number(id), result);
      setOfflineSyncState(SYNC_STATES.PENDING);

      // Generate post-calculation verification notes
      const postValidation = generateVerificationNotes(mergedPkg, localEntries, localSales, localAdj, result);
      setValidationReport(postValidation);
      if (postValidation.warnings.length > 0) {
        setStepValidation({ errors: [], warnings: postValidation.warnings.map(w => w.message) });
      } else {
        setStepValidation({ errors: [], warnings: [] });
      }

      toast({ title: 'Calculated Locally', description: `Closing stock: £${result.total_value?.toFixed(2)}` });
    } catch (err) {
      console.error('[Calculate] ERROR:', err);
      toast({ title: 'Calculation Failed', description: err.message, variant: 'destructive' });
    } finally { setCalculating(false); }
  };

  // ── Offline: Run local validation without calculation ──
  const handleRunLocalValidation = useCallback(() => {
    if (!offlinePkg?.package) return null;
    const record = offlinePkg;
    const localEntries = record?.local_changes?.count_entries ||
      Object.entries(entriesRef.current).map(([lineId, entry]) => ({
        count_sheet_line_id: Number(lineId),
        quantity: entry.quantity != null && entry.quantity !== '' ? Number(entry.quantity) : null,
        unit_id: null,
        unit_label: entry.unit_label || null,
      }));
    const localSales = record?.local_changes?.sales_entries || record?.package?.sales_entries || [];
    const localAdj = record?.local_changes?.adjustment_entries || record?.package?.adjustment_entries || [];
    const report = generateVerificationNotes(record.package, localEntries, localSales, localAdj, offlineCalcResult);
    setValidationReport(report);
    return report;
  }, [offlinePkg, offlineCalcResult]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-500" />
      </div>
    );
  }

  if (!session) return null;

  const StepIcon = currentStepDef.icon;
  // Wave 5A P6: PSB historical sessions always display "Completed — Imported history"
  // regardless of the underlying status value, to avoid internal terminology leaking to users.
  const statusCfg = session.is_psb_historical
    ? { label: 'Completed \u2014 Imported history', className: 'bg-teal-100 text-teal-800', icon: History }
    : (STATUS_CONFIG[session.status] || STATUS_CONFIG.draft);
  const StatusIcon = statusCfg.icon;

  return (
    <div className="space-y-5" data-testid="session-wizard-page">
      <Toaster />

      {/* Wave 5A P6 — PSB Historical Import read-only notice */}
      {session.is_psb_historical && (
        <>
          {/* Plain-English read-only banner — no internal PSB/Route terminology */}
          <div
            className="flex items-start gap-3 px-4 py-3 bg-teal-50 border border-teal-300 rounded-lg"
            data-testid="psb-historical-banner"
            role="note"
          >
            <Lock className="h-5 w-5 text-teal-600 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-semibold text-teal-900" data-testid="psb-historical-title">
                Imported historical stocktake — Read only
              </p>
              <p className="text-xs text-teal-800 mt-0.5" data-testid="psb-historical-desc">
                This session was created from a historical archive and is permanently locked.
                The data reflects the original period exactly as accepted. Editing, recalculating,
                and publishing are not available on imported historical sessions.
              </p>
            </div>
          </div>

          {/* Concise source and reconciliation summary */}
          <Card className="border-teal-200 bg-teal-50/30" data-testid="psb-historical-source-summary">
            <CardContent className="pt-4 pb-3">
              <div className="flex flex-wrap gap-6 text-sm">
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Site</p>
                  <p className="font-medium text-slate-800 mt-0.5">{session.site_name || '—'}</p>
                </div>
                {(session.period_from || session.period_to) && (
                  <div>
                    <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Period covered</p>
                    <p className="font-medium text-slate-800 mt-0.5">
                      {formatDDMMYYYY(session.period_from)} to {formatDDMMYYYY(session.period_to)}
                    </p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Products</p>
                  <p className="font-medium text-slate-800 mt-0.5">{lines.length}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide">Imported</p>
                  <p className="font-medium text-slate-800 mt-0.5">{formatDDMMYYYY(session.session_date)}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Admin Evidence Drawer — technical detail kept Admin-only */}
          {isAdmin && (
            <Collapsible open={adminEvidenceOpen} onOpenChange={setAdminEvidenceOpen}>
              <CollapsibleTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-2 border-slate-300 text-slate-600"
                  data-testid="psb-admin-evidence-drawer-toggle"
                >
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Admin — Technical Evidence
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${adminEvidenceOpen ? 'rotate-180' : ''}`} />
                </Button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <Card className="border-slate-200 bg-slate-50 mt-2" data-testid="psb-admin-evidence-drawer">
                  <CardContent className="pt-4 pb-3 space-y-3">
                    <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Admin — Internal pipeline references
                    </p>
                    {session.psb_s8_run_id && (
                      <div>
                        <p className="text-xs text-muted-foreground">Stage 8 dispatch run</p>
                        <p className="text-xs font-mono bg-slate-100 border border-slate-200 rounded px-2 py-1 mt-0.5 text-slate-700 break-all" data-testid="psb-s8-run-id">
                          {session.psb_s8_run_id}
                        </p>
                      </div>
                    )}
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Link
                        to="/stocktaking/historical-conversion"
                        data-testid="psb-evidence-reconciliation-link"
                      >
                        <Button variant="outline" size="sm" className="text-xs">
                          <ExternalLink className="h-3 w-3 mr-1.5" />
                          Reconciliation &amp; Stage 7 audit
                        </Button>
                      </Link>
                      <Link
                        to="/stocktaking/psb-stage1-import"
                        data-testid="psb-evidence-diagnostic-link"
                      >
                        <Button variant="outline" size="sm" className="text-xs">
                          <ExternalLink className="h-3 w-3 mr-1.5" />
                          View import evidence
                        </Button>
                      </Link>
                    </div>
                  </CardContent>
                </Card>
              </CollapsibleContent>
            </Collapsible>
          )}
        </>
      )}

      {/* iter593 OCC — Stale Conflict Banner */}
      {conflictError && (
        <div
          className="flex items-center justify-between px-4 py-3 bg-red-50 border border-red-300 rounded-lg"
          data-testid="stale-conflict-banner"
          role="alert"
        >
          <div className="flex items-start gap-3 min-w-0">
            <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-sm font-semibold text-red-800" data-testid="stale-conflict-title">
                Newer count data exists
              </p>
              <p className="text-xs text-red-700 mt-0.5" data-testid="stale-conflict-message">
                Another session or tab has saved newer counts for{' '}
                {conflictError.conflicts?.length === 1
                  ? '1 line'
                  : `${conflictError.conflicts?.length ?? 'some'} lines`}
                . Reload to see the latest data before saving, or dismiss to keep your local changes
                (your next save will overwrite the server data).
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 ml-4 shrink-0">
            <Button
              size="sm"
              variant="outline"
              className="border-red-400 text-red-700 hover:bg-red-100"
              data-testid="stale-conflict-reload-btn"
              onClick={async () => {
                setConflictError(null);
                await loadSession();
              }}
            >
              <RotateCcw className="h-3 w-3 mr-1.5" />
              Reload
            </Button>
            <Button
              size="sm"
              variant="ghost"
              className="text-red-600 hover:text-red-800"
              data-testid="stale-conflict-dismiss-btn"
              onClick={() => setConflictError(null)}
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}

      {/* Offline Mode Banner */}
      {isOfflineMode && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-lg" data-testid="offline-mode-banner">
          <div className="flex items-center gap-2">
            <WifiOff className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-medium text-amber-800">Working Offline</span>
            {offlineSyncState === SYNC_STATES.PENDING && (
              <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">Pending Sync</Badge>
            )}
            {offlineSyncState === SYNC_STATES.CONFLICT && (
              <Badge variant="destructive" className="text-[10px]">Conflict — see below</Badge>
            )}
          </div>
          <div className="flex items-center gap-2">
            {isOnline && offlineSyncState === SYNC_STATES.PENDING && (
              <Button size="sm" variant="outline" onClick={handleSyncToServer} disabled={syncing} data-testid="sync-session-btn">
                <CloudUpload className={`h-3 w-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                {syncing ? 'Syncing...' : 'Sync to Server'}
              </Button>
            )}
            {isOnline && offlineSyncState === SYNC_STATES.CONFLICT && (
              <Button size="sm" variant="outline" onClick={handleSyncToServer} disabled={syncing} data-testid="retry-sync-btn">
                <RefreshCw className={`h-3 w-3 mr-1 ${syncing ? 'animate-spin' : ''}`} />
                Retry Sync
              </Button>
            )}
            <Button size="sm" variant="ghost" className="text-amber-700" onClick={() => setShowDiagnostics(v => !v)} data-testid="toggle-diagnostics-btn">
              <HardDrive className="h-3 w-3 mr-1" />{showDiagnostics ? 'Hide' : 'Package Info'}
            </Button>
          </div>
        </div>
      )}

      {/* Offline package diagnostics */}
      {isOfflineMode && showDiagnostics && (
        <OfflinePackageDiagnostics sessionId={id} />
      )}

      {/* Header */}
      <div className="flex flex-col gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => {
            if (isChildSession && session?.parent_session_id) {
              navigate(`/stocktaking/sessions/${session.parent_session_id}`);
            } else if (isOfflineMode) {
              navigate('/stocktaking/offline');
            } else {
              navigate('/stocktaking/sessions');
            }
          }} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-slate-900">
                {session.is_psb_historical ? 'Imported historical stocktake' : session.session_reference}
              </h1>
              {session.is_psb_historical && (
                <span className="text-sm font-normal text-slate-400">— Read only</span>
              )}
              <Badge className={`text-[10px] ${statusCfg.className}`}>
                <StatusIcon className="h-3 w-3 mr-1" />{statusCfg.label}
              </Badge>
              <Badge variant="outline" className="text-[10px]">{isStockAudit ? 'Stock Audit' : 'Valuation'}</Badge>
              {isMultiLocation && (
                <Badge variant="outline" className="text-[10px] bg-indigo-50 text-indigo-700 border-indigo-200">
                  {session.audit_mode === 'multi_with_cellar' ? 'Multi + Cellar' : 'Multi-Location'}
                </Badge>
              )}
              {isChildSession && session.audit_location_name && (
                <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
                  {session.audit_location_name}
                </Badge>
              )}
              {isOfflineMode && offlineSyncState === SYNC_STATES.SYNCED && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" data-testid="sync-state-synced">
                  <CheckCircle className="h-3 w-3 mr-1" />Synced
                </Badge>
              )}
              {isOfflineMode && offlineSyncState === SYNC_STATES.PENDING && (
                <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]" data-testid="sync-state-pending">
                  <AlertTriangle className="h-3 w-3 mr-1" />Pending Sync
                </Badge>
              )}
              {isOfflineMode && offlineSyncState === SYNC_STATES.CONFLICT && (
                <Badge variant="destructive" className="text-[10px]" data-testid="sync-state-conflict">
                  <AlertTriangle className="h-3 w-3 mr-1" />Conflict
                </Badge>
              )}
              {isAvailableOffline && !isOfflineMode && (
                <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]" data-testid="offline-available-badge">
                  <HardDrive className="h-3 w-3 mr-1" />Offline Ready
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-0.5">
              {/* iter426C #9 — DD/MM/YYYY display format */}
              {/* Wave 5A P6: for PSB historical, show the session reference here since the
                  title is replaced with a plain-English label. Regular sessions show site/date. */}
              {session.is_psb_historical
                ? session.session_reference
                : `${session.site_name} | ${formatDDMMYYYY(session.session_date)}${session.period_from ? ` | Period: ${formatDDMMYYYY(session.period_from)} to ${formatDDMMYYYY(session.period_to)}` : ''}`
              }
            </p>
            {/* iter436 — multi-location breadcrumb: only visible when
                this is a CHILD session of a multi-location audit.
                Lets the auditor jump between Cellar/Bar/etc. without
                going back to the parent page. */}
            {isChildSession && (
              <div className="mt-2">
                <LocationBreadcrumb session={session} />
              </div>
            )}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* iter340 v5 — Site Inventory Setup floating window. Visible
              everywhere in the wizard (online + offline) so the auditor
              can review/expand the product list and see master vs site
              measure/yield sources without leaving the wizard.
              Wave 5A P6: hidden for PSB historical sessions (read-only, no editable inventory). */}
          {!session.is_psb_historical && (
          <Button
            size="sm"
            variant="outline"
            onClick={() => setSiteInventorySetupOpen(true)}
            data-testid="site-inventory-setup-btn"
          >
            <Settings2 className="h-3 w-3 mr-1" />
            Site Inventory Setup
          </Button>
          )}
          {/* 2026-02 — single clean Summary shortcut, top action row.
              Replaces the old bottom "Summary Stock Audit Report" panel.
              Linked session -> opens that exact Summary wizard directly
              (no Report Centre hop). Calculated, unlinked session ->
              guarded launch then straight into the new draft. Hidden
              entirely until eligibility is known, so it never shows a
              confusing disabled state. */}
          {calculation && !isOfflineMode && !session.is_psb_historical && summaryLinkInfo && (
            summaryLinkInfo.status === 'linked'
            || (summaryLinkInfo.status === 'eligible_no_report' && (summaryLinkInfo.launch_eligible || summaryLinkInfo.job_id))
            // 2026-02 correction: no exact-date Job at all (resolver status
            // 'none') must still offer the draft when a saved calculation
            // makes it launch_eligible — Job attachment becomes an explicit
            // later step, never a precondition for starting the draft.
            || (summaryLinkInfo.status === 'none' && summaryLinkInfo.launch_eligible)
          ) && (
            <Button
              size="sm"
              variant="outline"
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-100"
              onClick={handleTopSummaryClick}
              disabled={launchingSummaryTop}
              data-testid="top-summary-shortcut-btn"
            >
              {launchingSummaryTop ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <FileText className="h-3 w-3 mr-1" />}
              {summaryLinkInfo.status === 'linked'
                ? 'Open Summary'
                : summaryLinkInfo.launch_eligible
                  ? (summaryLinkInfo.session_locked ? 'Launch Summary' : 'Start Summary Draft')
                  : 'Create Summary Report'}
            </Button>
          )}
          {summaryLinkLoading && calculation && !isOfflineMode && !session.is_psb_historical && !summaryLinkInfo && (
            <Button size="sm" variant="outline" disabled data-testid="top-summary-shortcut-loading-btn">
              <Loader2 className="h-3 w-3 mr-1 animate-spin" />Summary…
            </Button>
          )}
          {/* Unlock button — Admin only, locked sessions only (Check 11) */}
          {canUnlockSession && !isOfflineMode && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-400 text-amber-700 hover:bg-amber-50"
              onClick={() => setShowUnlockConfirm(true)}
              disabled={unlocking}
              data-testid="admin-unlock-session-btn"
              title="Admin only: unlock this locked session and revert it to In Progress."
            >
              {unlocking ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Unlock className="h-3 w-3 mr-1" />}
              Unlock Session
            </Button>
          )}
          {/* Unlock confirmation dialog */}
          <AlertDialog open={showUnlockConfirm} onOpenChange={setShowUnlockConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Unlock Locked Session?</AlertDialogTitle>
                <AlertDialogDescription>
                  This will revert <strong>{session?.session_reference}</strong> from{' '}
                  <strong>Locked</strong> back to <strong>In Progress</strong>. The action will
                  be recorded in the immutable audit trail. Only Administrators can perform this
                  unlock. Are you sure you want to continue?
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="unlock-confirm-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleUnlockSession}
                  disabled={unlocking}
                  className="bg-amber-600 hover:bg-amber-700"
                  data-testid="unlock-confirm-proceed"
                >
                  {unlocking ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Yes, Unlock Session
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* 2026-02 — inline nearest-date acknowledgement for the top-row
              Summary shortcut (mirrors StocktakeReportsDialog's own D8
              dialog so the behaviour is identical either way the launch is
              triggered), so confirming launches straight into the new
              Summary wizard without a detour through Report Centre. */}
          <AlertDialog open={!!nearestAckInfo} onOpenChange={(v) => !v && setNearestAckInfo(null)}>
            <AlertDialogContent data-testid="top-summary-nearest-ack-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle>Nearby Summary exists</AlertDialogTitle>
                <AlertDialogDescription>
                  {nearestAckInfo?.message || 'A Summary near this date exists but is not an exact match.'}
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel data-testid="top-summary-nearest-ack-cancel">Cancel</AlertDialogCancel>
                <AlertDialogAction
                  data-testid="top-summary-nearest-ack-confirm"
                  disabled={launchingSummaryTop}
                  onClick={() => launchSummaryAndNavigate(true)}
                >
                  {launchingSummaryTop ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  Proceed and create a separate Summary
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>

          {/* Item 9 — Price Coverage Hard Stop dialog */}
          <Dialog open={showPriceCoverageDialog} onOpenChange={setShowPriceCoverageDialog}>
            <DialogContent className="max-w-lg" data-testid="price-coverage-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                  Incomplete Price Coverage — Cannot Lock
                </DialogTitle>
                <DialogDescription>
                  {priceCoverageError?.message}
                </DialogDescription>
              </DialogHeader>
              {priceCoverageError?.unpriced_products?.length > 0 && (
                <div className="max-h-56 overflow-y-auto border rounded text-xs" data-testid="unpriced-products-list">
                  <table className="w-full">
                    <thead className="sticky top-0 bg-slate-50 border-b">
                      <tr>
                        <th className="text-left py-1.5 px-2 font-semibold">Code</th>
                        <th className="text-left py-1.5 px-2 font-semibold">Product Name</th>
                      </tr>
                    </thead>
                    <tbody>
                      {priceCoverageError.unpriced_products.map((p, i) => (
                        <tr key={i} className="border-b border-slate-50 hover:bg-slate-50">
                          <td className="py-1 px-2 font-mono text-slate-500">{p.product_code || '—'}</td>
                          <td className="py-1 px-2">{p.product_name}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {priceCoverageError.unpriced_count > 50 && (
                    <p className="text-center py-1.5 text-muted-foreground italic">
                      …and {priceCoverageError.unpriced_count - 50} more
                    </p>
                  )}
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowPriceCoverageDialog(false)} data-testid="price-coverage-cancel-btn">
                  Cancel — Fix Prices First
                </Button>
                {priceCoverageError?.can_force_override && (
                  <Button
                    variant="destructive"
                    onClick={() => {
                      setShowPriceCoverageDialog(false);
                      handlePublish(true);
                    }}
                    data-testid="price-coverage-force-lock-btn"
                  >
                    Lock Anyway (Admin Override)
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {/* Release Contract S7 — Cost-Basis Review hard stop dialog */}
          <Dialog open={showCostBasisDialog} onOpenChange={(open) => { setShowCostBasisDialog(open); if (!open) setCostBasisOverrideReason(''); }}>
            <DialogContent className="max-w-lg" data-testid="cost-basis-review-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 text-amber-700">
                  <AlertTriangle className="h-5 w-5" />
                  Cost-Basis Review Required — Cannot Lock
                </DialogTitle>
                <DialogDescription>
                  {costBasisError?.message}
                  {costBasisError?.affected_product_count != null && costBasisError?.total_product_count != null && (
                    <span className="block mt-1 text-xs text-muted-foreground">
                      {costBasisError.affected_product_count} of {costBasisError.total_product_count} product(s) affected.
                    </span>
                  )}
                </DialogDescription>
              </DialogHeader>
              {costBasisError?.can_force_override && (
                <div className="space-y-1.5">
                  <label className="text-xs font-medium text-slate-700">Override reason (required)</label>
                  <Textarea
                    value={costBasisOverrideReason}
                    onChange={e => setCostBasisOverrideReason(e.target.value)}
                    placeholder="Explain why it is safe to publish despite the unresolved cost-basis review…"
                    rows={3}
                    data-testid="cost-basis-override-reason-input"
                  />
                </div>
              )}
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setShowCostBasisDialog(false)} data-testid="cost-basis-cancel-btn">
                  Cancel — Review First
                </Button>
                {costBasisError?.can_force_override && (
                  <Button
                    variant="destructive"
                    disabled={!costBasisOverrideReason.trim()}
                    onClick={() => {
                      setShowCostBasisDialog(false);
                      handlePublish(false, true, costBasisOverrideReason.trim());
                    }}
                    data-testid="cost-basis-force-lock-btn"
                  >
                    Lock Anyway (Admin Override)
                  </Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>
          {/* iter360 — Revert completed → in_progress (admin/scheduler) */}
          {canRevertToInProgress && !isOfflineMode && (
            <Button
              size="sm"
              variant="outline"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              onClick={handleRevertToInProgress}
              disabled={reverting}
              data-testid="revert-to-in-progress-btn"
              title="Move this completed session back to In Progress so you can edit opening stock, counts or recalculate."
            >
              {reverting ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RotateCcw className="h-3 w-3 mr-1" />}
              Revert to In Progress
            </Button>
          )}
          {/* Download for Offline button — only show when online */}
          {!isOfflineMode && isOnline && (
            <Button size="sm" variant="outline" onClick={handleDownloadForOffline} disabled={downloading} data-testid="download-offline-btn">
              {downloading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Download className="h-3 w-3 mr-1" />}
              {isAvailableOffline ? 'Re-download' : 'Download for Offline'}
            </Button>
          )}
          {/* Print Paper Count Sheet */}
          {!isOfflineMode && isOnline && (
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                try {
                  toast({ title: 'Generating count sheet...', description: 'Please wait' });
                  const response = await api.get(`/count/sessions/${session.id}/paper-count-sheet`, { responseType: 'blob' });
                  const blob = new Blob([response.data], { type: 'application/pdf' });
                  const url = URL.createObjectURL(blob);
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `count_sheet_${session.session_reference || session.id}.pdf`;
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                  setTimeout(() => URL.revokeObjectURL(url), 60000);
                  toast({ title: 'Count sheet downloaded', description: `count_sheet_${session.session_reference || session.id}.pdf` });
                } catch (err) {
                  console.error('Print count sheet error:', err);
                  toast({ title: 'Download failed', description: err?.message || 'Could not generate count sheet', variant: 'destructive' });
                }
              }}
              data-testid="paper-count-sheet-btn"
            >
              <FileDown className="h-3 w-3 mr-1" />Print Count Sheet
            </Button>
          )}
          {/* Reports button: opens the Report Centre dialog so auditors
              can pull Summary, Core Stock Audit, Valuation and
              Supporting Schedule reports straight from the wizard. */}
          {!isOfflineMode && isOnline && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setReportsDialogOpen(true)}
              data-testid="wizard-reports-btn"
            >
              <FileText className="h-3 w-3 mr-1" />Reports
            </Button>
          )}
          {/* Findings & Actions button hidden from the normal workflow —
              the Summary Wizard's General Audit Comments + Actions
              Agreed are the operational process for now. Model/routes/
              dialog are untouched and still reachable directly. */}
          {/* Wave 5A P6: Edit Dates button is hidden for PSB historical sessions (permanently read-only).
              For regular sessions it is disabled when not editable. */}
          {!editing && !isOfflineMode && !session.is_psb_historical ? (
            <Button size="sm" variant="ghost" onClick={startEditing} disabled={!isEditable} data-testid="edit-session-btn">
              <Pencil className="h-3 w-3 mr-1" />Edit Dates
            </Button>
          ) : !isOfflineMode && !session.is_psb_historical && editing && (
            <div className="flex gap-1">
              <Button size="sm" variant="default" onClick={saveEditing} data-testid="save-edit-btn">
                <Save className="h-3 w-3 mr-1" />Save
              </Button>
              <Button size="sm" variant="ghost" onClick={cancelEditing}><X className="h-3 w-3" /></Button>
            </div>
          )}
        </div>
      </div>

      {/* Edit Form (collapsible) */}
      {editing && (
        <Card className="border-indigo-200 bg-indigo-50/30">
          <CardContent className="pt-4 pb-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {isStockAudit ? (
                <>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Opening Date (Period From)</Label>
                    <Input type="date" value={editForm.period_from} onChange={e => setEditForm(f => ({ ...f, period_from: e.target.value }))} data-testid="edit-period-from" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-muted-foreground">Closing Date (Period To)</Label>
                    <Input type="date" value={editForm.period_to} onChange={e => setEditForm(f => ({ ...f, period_to: e.target.value, session_date: e.target.value }))} data-testid="edit-period-to" />
                  </div>
                </>
              ) : (
                <div className="space-y-1">
                  <Label className="text-xs text-muted-foreground">Count Date</Label>
                  <Input type="date" value={editForm.session_date} onChange={e => setEditForm(f => ({ ...f, session_date: e.target.value }))} data-testid="edit-session-date" />
                </div>
              )}
              <div className="space-y-1">
                <Label className="text-xs text-muted-foreground">Auditor</Label>
                <Select value={editForm.auditor_id || 'unassigned'} onValueChange={v => setEditForm(f => ({ ...f, auditor_id: v === 'unassigned' ? '' : v }))}>
                  <SelectTrigger data-testid="edit-auditor-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="unassigned">Unassigned</SelectItem>
                    {auditors.map(a => <SelectItem key={a.user_id || a.id} value={String(a.user_id || a.id)}>{a.first_name} {a.last_name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1 sm:col-span-2">
                <Label className="text-xs text-muted-foreground">Notes</Label>
                <Textarea value={editForm.notes} onChange={e => setEditForm(f => ({ ...f, notes: e.target.value }))} placeholder="Session notes" rows={2} data-testid="edit-notes" />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Multi-Location Parent Dashboard — shown for parent multi-location sessions */}
      {isMultiLocation && (
        <ParentAuditDashboard session={session} auditors={auditors} isOffline={isOfflineMode} />
      )}

      {/* Wizard — only for single-site sessions and child location sessions */}
      {!isMultiLocation && (
      <>
      {/* iter372 — Wizard step shortcuts (Alt+N / Alt+P, ⌥ on Mac) */}
      <WizardShortcutHandler
        onNext={handleNextStep}
        onPrev={handlePrevStep}
        canPrev={wizardStep > 0}
        canNext={wizardStep < steps.length - 1}
      />
      {/* Top Save & Next row — Check 6 */}
      {/* Wave 5A P6: PSB historical shows "Next" only (read-only), not "Save & Next" */}
      {wizardStep < steps.length - 1 && (
        <div className="flex justify-end" data-testid="top-save-next-row">
          {session.is_psb_historical ? (
            <Button
              size="sm"
              onClick={handleNextStep}
              data-testid="top-next-step-btn"
            >
              Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={handleNextStep}
              disabled={saving}
              data-testid="top-next-step-btn"
            >
              {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Save &amp; Next <ChevronRight className="h-4 w-4 ml-1" />
            </Button>
          )}
        </div>
      )}
      {/* Wizard Progress Bar */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <WizardProgressBar steps={steps} currentStep={wizardStep} onStepClick={handleStepClick} />
        </CardContent>
      </Card>

      {/* Step Header */}
      <div className="flex items-center gap-3 px-1">
        <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-indigo-100 text-indigo-700">
          <StepIcon className="h-5 w-5" />
        </div>
        <div className="flex-1">
          <h2 className="text-base font-semibold text-slate-900">
            Step {wizardStep + 1}: {currentStepDef.label}
          </h2>
          <p className="text-xs text-muted-foreground">{currentStepDef.description}</p>
        </div>
        {/* iter372 — small shortcut legend chip */}
        <WizardShortcutLegend />
      </div>

      {/* Step Validation Messages */}
      <StepValidation warnings={stepValidation.warnings} errors={stepValidation.errors} />

      {/* ══ STEP CONTENT ══ */}

      {/* Step: Opening Stock */}
      {currentStepDef.key === 'opening' && (
        <div className="space-y-4">
          <OpeningStockPanel
            ref={openingRef}
            sessionId={session.id}
            siteId={session.site_id}
            status={session.status}
            sessionDate={session.session_date}
            offlineData={isOfflineMode ? offlinePkg?.package : null}
          />
          {!session.period_from && isStockAudit && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-4 text-xs text-amber-700">
                <AlertTriangle className="h-4 w-4 inline mr-1" />
                Set the audit period dates (Opening Date and Closing Date) using the Edit button above before proceeding.
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* Step: Deliveries & Credits */}
      {currentStepDef.key === 'deliveries' && (
        <div className="space-y-4">
          {!isOfflineMode && (
            <DeliveryDocumentImportPanel sessionId={session.id} isEditable={isEditable} />
          )}
          <div id="native-delivery-entry-panel" data-testid="native-delivery-entry-panel">
          <WizardDeliveriesCreditsPanel
            ref={deliveriesRef}
            siteId={session.site_id}
            sessionId={session.id}
            periodFrom={session.period_from}
            periodTo={session.period_to}
            isEditable={isEditable}
            offlineData={isOfflineMode ? offlinePkg?.package : null}
            offlineLocalChanges={isOfflineMode ? offlinePkg?.local_changes : null}
            isOfflineMode={isOfflineMode}
            onOfflineDeliveriesChange={async (type, items) => {
              if (!offlinePkg) return;
              const key = type === 'delivery' ? 'offline_deliveries' : 'offline_credits';
              await stocktakeOfflineService.saveLocalChanges(Number(id), { [key]: items });
              // Also update the local ref so re-renders pick it up
              if (offlinePkg.local_changes) {
                offlinePkg.local_changes[key] = items;
              } else {
                offlinePkg.local_changes = { [key]: items };
              }
              setDirty(true);
            }}
          />
          </div>
        </div>
      )}

      {/* Step: Sales */}
      {currentStepDef.key === 'sales' && (
        <div className="space-y-4">
          <WizardSalesPanel
            ref={salesRef}
            sessionId={session.id}
            siteId={session.site_id}
            periodFrom={session.period_from}
            periodTo={session.period_to}
            isEditable={isEditable}
            isOfflineMode={isOfflineMode}
            offlineData={isOfflineMode ? offlinePkg?.package : null}
            vatRate={vatRate}
            accountsSales={accountsSales}
            onAccountsSalesChange={handleAccountsSalesChange}
          />
        </div>
      )}

      {/* Step: Issues to Locations (cellar only) */}
      {currentStepDef.key === 'issues' && (
        <div className="space-y-4">
          <CellarIssuesPanel
            session={session}
            parentSessionId={session.parent_session_id}
            locationId={session.audit_location_id}
            isOfflineMode={isOfflineMode}
          />
        </div>
      )}

      {/* Step: Transfers (non-cellar location only) */}
      {currentStepDef.key === 'transfers' && (
        <div className="space-y-4">
          <LocationTransfersPanel
            session={session}
            parentSessionId={session.parent_session_id}
            locationId={session.audit_location_id}
            isOfflineMode={isOfflineMode}
          />
        </div>
      )}

      {/* Step: Adjustments */}
      {currentStepDef.key === 'adjustments' && (
        <div className="space-y-4">
          <AdjustmentsPanel
            ref={adjustmentsRef}
            sessionId={session.id}
            siteId={session.site_id}
            status={session.status}
            sessionDate={session.session_date}
            isEditable={isEditable}
            wizardMode
            isOfflineMode={isOfflineMode}
            offlineData={isOfflineMode ? offlinePkg?.package : null}
          />
        </div>
      )}

      {/* Step: Closing Stock — Review / Confirmation / Targeted Refinement */}
      {currentStepDef.key === 'closing' && (
        <div className="space-y-4">
          {/* AbbeyZone Count callout + Import */}
          <Card className="border-cyan-200 bg-cyan-50/30" data-testid="closing-count-callout">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-start gap-3">
                <div className="bg-cyan-100 p-2 rounded-lg shrink-0 mt-0.5">
                  <Tablet className="h-5 w-5 text-cyan-700" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-cyan-900">Primary counting is done in AbbeyZone Count</p>
                  <p className="text-xs text-cyan-700 mt-0.5">
                    Download and count on your tablet, then transfer the count file via USB cable and import below.
                  </p>
                  <div className="mt-2.5 flex items-center gap-2">
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept=".json"
                        className="hidden"
                        data-testid="import-count-file-input"
                        onChange={async (e) => {
                          const file = e.target.files?.[0];
                          if (!file) return;
                          try {
                            const text = await file.text();
                            const result = await handleImportCountData(text);
                            if (result) toast({ title: result.title, description: result.description });
                          } catch (err) {
                            toast({ title: 'Import failed', description: err.message, variant: 'destructive' });
                          }
                          e.target.value = '';
                        }}
                      />
                      <Button variant="default" size="sm" className="gap-1.5 pointer-events-none" tabIndex={-1} data-testid="import-count-file-btn">
                        <Upload className="h-3.5 w-3.5" />
                        Import Count File
                      </Button>
                    </label>
                    <Button
                      variant="outline"
                      size="sm"
                      className="gap-1.5 text-xs"
                      onClick={async () => {
                        try {
                          const text = await navigator.clipboard.readText();
                          const result = await handleImportCountData(text);
                          if (result) toast({ title: result.title, description: result.description });
                        } catch {
                          toast({ title: 'Paste failed', description: 'No valid count data found on clipboard.', variant: 'destructive' });
                        }
                      }}
                      data-testid="paste-count-data-btn"
                    >
                      <ClipboardList className="h-3.5 w-3.5" />
                      Paste Count Data
                    </Button>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Count progress */}
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium text-slate-700 flex items-center gap-2">
                  Count Progress
                  {isOfflineMode && <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border bg-amber-100 text-amber-700 border-amber-200" data-testid="data-source-package">Package/IndexedDB</span>}
                </p>
                <p className="text-sm font-bold">{countedLines}/{lines.length}</p>
              </div>
              <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                <div
                  className="h-full bg-emerald-500 transition-all duration-300 rounded-full"
                  style={{ width: `${lines.length > 0 ? (countedLines / lines.length) * 100 : 0}%` }}
                />
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">
                {lines.length > 0 ? Math.round((countedLines / lines.length) * 100) : 0}% complete
                {countedLines > 0 && countedLines < lines.length && (
                  <span className="ml-2 text-amber-600">
                    {lines.length - countedLines} uncounted product{lines.length - countedLines !== 1 ? 's' : ''} will be treated as zero
                  </span>
                )}
              </p>
            </CardContent>
          </Card>

          {/* Targeted corrections — editable count grid */}
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ClipboardCheck className="h-4 w-4 text-slate-600" />
                    Review & Correct
                  </CardTitle>
                  <CardDescription className="text-xs">
                    Edit any quantity below for targeted corrections. All fields remain editable until the session is locked.
                  </CardDescription>
                </div>
                {/* iter340 v5 — "Add from inventory master" moved to the
                    Site Inventory Setup floating window in the wizard
                    header. Single point of entry across all wizard steps. */}
                {/* iter379 — "Refresh Data from Master" — re-pulls live
                    product names, default unit labels and missing
                    costs into the count sheet snapshots. Use this when
                    AbbeyZone Count synced earlier with stale data
                    (e.g. CAPS legacy names, products without units). */}
                {isEditable && !isOfflineMode && (
                  <div className="flex items-center gap-2 shrink-0">
                    {/* iter595 — Pending-save indicator: shows while count entries are
                        queued for autosave (dirty=true) so auditors know to wait
                        briefly before navigating away. */}
                    {dirty && !saving && (
                      <span className="text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid="count-save-pending">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse inline-block" />
                        Saving…
                      </span>
                    )}
                    {saving && (
                      <span className="text-[10px] text-indigo-700 bg-indigo-50 border border-indigo-200 rounded px-1.5 py-0.5 flex items-center gap-1" data-testid="count-save-active">
                        <Loader2 className="h-2.5 w-2.5 animate-spin" />
                        Saving…
                      </span>
                    )}
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={handleHealSnapshots}
                    disabled={healing}
                    className="h-7 text-xs gap-1.5 shrink-0"
                    data-testid="refresh-master-data-btn"
                    title="Refresh stale product names / unit dropdowns / missing costs from the live master"
                  >
                    {healing ? (
                      <Loader2 className="h-3 w-3 animate-spin" />
                    ) : (
                      <RefreshCw className="h-3 w-3" />
                    )}
                    Refresh from Master
                  </Button>
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {/* Search + Category/Subcategory filters */}
              {lines.length > 0 && (
                <div className="mb-4 space-y-2">
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                      <Input value={closingSearch} onChange={e => setClosingSearch(e.target.value)}
                        placeholder="Search by product name or code..." className="pl-8 h-8 text-xs" data-testid="closing-stock-search"
                        autoComplete="off" autoCorrect="off" spellCheck={false} name="closing-stock-search-input" />
                    </div>
                  </div>
                  {(closingAvailableCategories.length > 0 || closingAvailableSubcategories.length > 0) && (
                    <div className="space-y-1.5 p-2 bg-slate-50 rounded border border-slate-100 text-xs">
                      {closingAvailableCategories.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium text-slate-500 mb-1">Category</p>
                          <div className="flex flex-wrap gap-1">
                            {closingAvailableCategories.map(cat => (
                              <button key={cat}
                                onClick={() => setClosingSelectedCategories(prev => prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat])}
                                className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                                  closingSelectedCategories.includes(cat) ? 'bg-cyan-700 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                                }`} data-testid={`closing-cat-${cat}`}>{cat.replace(/_/g, ' ')}</button>
                            ))}
                          </div>
                        </div>
                      )}
                      {closingAvailableSubcategories.length > 0 && (
                        <div>
                          <p className="text-[10px] font-medium text-slate-500 mb-1">Sub-category</p>
                          <div className="flex flex-wrap gap-1">
                            {closingAvailableSubcategories.map(sub => (
                              <button key={sub}
                                onClick={() => setClosingSelectedSubcategories(prev => prev.includes(sub) ? prev.filter(s => s !== sub) : [...prev, sub])}
                                className={`px-2 py-0.5 rounded-full text-[10px] font-medium transition-colors ${
                                  closingSelectedSubcategories.includes(sub) ? 'bg-cyan-700 text-white' : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-100'
                                }`} data-testid={`closing-subcat-${sub}`}>{sub}</button>
                            ))}
                          </div>
                        </div>
                      )}
                      {closingActiveFilterCount > 0 && (
                        <button onClick={() => { setClosingSelectedCategories([]); setClosingSelectedSubcategories([]); }}
                          className="text-[10px] text-slate-500 hover:text-slate-700 flex items-center gap-0.5" data-testid="closing-clear-filters">
                          <X className="w-2.5 h-2.5" /> Clear filters ({closingActiveFilterCount})
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {lines.length === 0 ? (
                <div className="py-8 text-center">
                  <ClipboardList className="h-10 w-10 mx-auto mb-3 text-muted-foreground opacity-30" />
                  <p className="font-medium text-muted-foreground">No count sheet lines</p>
                  <p className="text-sm text-muted-foreground mt-1">
                    This site has no active inventory items. Add products to the site inventory first.
                  </p>
                  <Button variant="outline" className="mt-4" onClick={() => navigate('/stocktaking/site-inventory')}>Go to Site Inventory</Button>
                </div>
              ) : (
                Object.entries(groupedLines).map(([subcategory, catLines]) => (
                  <div key={subcategory} className="mb-4 last:mb-0" data-testid={`closing-subcategory-group-${subcategory.replace(/\s+/g, '-').toLowerCase()}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <span className="text-xs font-semibold text-slate-700 uppercase tracking-wider">{subcategory}</span>
                      <Badge variant="secondary" className="text-[10px]">{catLines.length}</Badge>
                      <Badge variant="outline" className="text-[10px] text-emerald-600">
                        {catLines.filter(l => entries[l.id]?.quantity !== undefined && entries[l.id]?.quantity !== '').length} counted
                      </Badge>
                    </div>
                    <div className="space-y-1.5">
                      {catLines.map(line => {
                        const entry = entries[line.id] || {};
                        const prod = productMap[line.beverage_product_id];
                        const prodUnits = prod?.units || [];
                        const hasUnits = prodUnits.length >= 1;
                        const selectedUnit = entry.unit_label || line.unit_label_snapshot || '';
                        const isCounted = entry.quantity !== undefined && entry.quantity !== '';

                        const getConversionInfo = () => {
                          if (!prod || !prod.case_size || prod.case_size <= 0) return null;
                          const qty = parseFloat(entry.quantity);
                          if (!qty || qty <= 0) return null;
                          const caseUnit = prod.units?.find(u => u.unit_type === 'case');
                          const singlesUnit = prod.units?.find(u => u.unit_type !== 'case');
                          if (!caseUnit || !singlesUnit) return null;
                          const selUnit = selectedUnit.toLowerCase();
                          if (selUnit === 'case') {
                            const singles = Math.round(qty * prod.case_size * 100) / 100;
                            return `= ${singles} ${singlesUnit.unit_type}${singles !== 1 ? 's' : ''}`;
                          } else if (selUnit === singlesUnit.unit_type.toLowerCase()) {
                            const cases = Math.round((qty / prod.case_size) * 10000) / 10000;
                            return `= ${cases} case${cases !== 1 ? 's' : ''}`;
                          }
                          return null;
                        };
                        const convInfo = getConversionInfo();

                        return (
                          <div
                            key={line.id}
                            className={`flex items-center gap-3 py-2 px-2 rounded-lg border ${
                              line.added_during_count
                                ? 'border-amber-300 bg-amber-50/70'
                                : isCounted ? 'border-slate-100 bg-white' : 'border-amber-100 bg-amber-50/50'
                            }`}
                            data-testid={`count-line-${line.id}`}
                          >
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                <p className="text-sm font-medium truncate">{line.product_name_snapshot}</p>
                                {line.added_during_count && (
                                  <Badge variant="outline" className="text-[9px] px-1 py-0 border-amber-400 text-amber-700 bg-amber-100 shrink-0" data-testid={`badge-added-during-count-${line.id}`}>
                                    Added During Count
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                {line.location_name_snapshot && (
                                  <span className="flex items-center gap-0.5"><MapPin className="h-3 w-3" />{line.location_name_snapshot}</span>
                                )}
                                {!hasUnits && line.unit_label_snapshot && (
                                  <span className="text-slate-400">({line.unit_label_snapshot})</span>
                                )}
                                {/* iter426 (Iteration B #5) — Purple yield
                                    description hidden on Closing Stock to
                                    avoid auditor confusion. The yield is
                                    still surfaced on the Delivery Note step
                                    where it actually informs cost-conversion. */}
                              </div>
                            </div>
                            {hasUnits ? (
                              <div className="w-28 flex-shrink-0">
                                <Select value={selectedUnit} onValueChange={(val) => handleEntryChange(line.id, 'unit_label', val)} disabled={!isEditable}>
                                  <SelectTrigger className="h-9 text-xs" data-testid={`unit-select-${line.id}`}>
                                    <SelectValue placeholder="Unit" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    {prodUnits.map(u => <SelectItem key={u.id} value={u.unit_type}>{u.unit_type}</SelectItem>)}
                                  </SelectContent>
                                </Select>
                              </div>
                            ) : (
                              <div className="w-20 flex-shrink-0 text-xs text-muted-foreground text-center">
                                {line.unit_label_snapshot || ''}
                              </div>
                            )}
                            <div className="w-44 flex-shrink-0">
                              {(() => {
                                const splits = entrySplits[line.id];
                                if (splits && splits.length > 0) {
                                  const needsReview = splitsNeedReview(splits, prodUnits);
                                  return (
                                    <div className="space-y-1" data-testid={`count-splits-${line.id}`}>
                                      {splits.map((s, i) => {
                                        const splitUnit = s.unit_id || resolveTargetUnitId(line.id);
                                        const u = prodUnits.find(pu => pu.id === splitUnit);
                                        const conv = u ? parseFloat(u.conversion_to_base) : null;
                                        const splitNeedsReview = s.unit_id && (!u || !conv || conv <= 0);
                                        return (
                                          <div key={s._key || i} className="flex items-center gap-1">
                                            <Input
                                              type="number"
                                              min="0"
                                              step="0.01"
                                              placeholder={`split ${i + 1}`}
                                              value={s.qty}
                                              onChange={e => handleSplitChange(line.id, i, e.target.value)}
                                              className="h-8 text-xs text-right tabular-nums flex-1 min-w-0"
                                              disabled={!isEditable}
                                              data-testid={`count-split-input-${line.id}-${i}`}
                                            />
                                            {hasUnits && prodUnits.length > 1 && (
                                              <Select
                                                value={splitUnit ? String(splitUnit) : ''}
                                                onValueChange={(val) => handleSplitUnitChange(line.id, i, val)}
                                                disabled={!isEditable}
                                              >
                                                <SelectTrigger
                                                  className={`h-8 text-[10px] w-16 px-1 ${splitNeedsReview ? 'border-amber-400 bg-amber-50' : ''}`}
                                                  data-testid={`count-split-unit-${line.id}-${i}`}
                                                >
                                                  <SelectValue placeholder="—" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  {prodUnits.map(pu => (
                                                    <SelectItem key={pu.id} value={String(pu.id)} className="text-xs">{pu.unit_type}</SelectItem>
                                                  ))}
                                                </SelectContent>
                                              </Select>
                                            )}
                                            <button
                                              type="button"
                                              onClick={() => handleRemoveSplit(line.id, i)}
                                              className="text-rose-400 hover:text-rose-700"
                                              title="Remove this split"
                                              data-testid={`count-split-remove-${line.id}-${i}`}
                                              disabled={!isEditable}
                                            >
                                              <X className="h-3 w-3" />
                                            </button>
                                          </div>
                                        );
                                      })}
                                      {needsReview && (
                                        <div
                                          className="text-[9.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5"
                                          data-testid={`count-splits-review-${line.id}`}
                                        >
                                          ⚠ One or more splits use a measure without a defined conversion — flagged for auditor review.
                                        </div>
                                      )}
                                      <div className="flex items-center justify-between gap-1 text-[10px]">
                                        <span className="text-slate-500 tabular-nums" data-testid={`count-splits-sum-${line.id}`}>
                                          = {sumSplits(splits, line.id).toLocaleString('en-GB', { maximumFractionDigits: 2 })} {selectedUnit || ''}
                                        </span>
                                        <button
                                          type="button"
                                          onClick={() => handleAddSplit(line.id)}
                                          className="text-indigo-600 hover:text-indigo-800 hover:underline font-medium"
                                          data-testid={`count-split-add-${line.id}`}
                                          disabled={!isEditable}
                                        >
                                          + split
                                        </button>
                                      </div>
                                    </div>
                                  );
                                }
                                return (
                                  <>
                                    <Input
                                      type="number"
                                      min="0"
                                      step="0.01"
                                      placeholder="Qty"
                                      value={entry.quantity ?? ''}
                                      onChange={e => handleEntryChange(line.id, 'quantity', e.target.value)}
                                      className="h-9 text-sm text-right tabular-nums"
                                      disabled={!isEditable}
                                      data-testid={`count-input-${line.id}`}
                                    />
                                    {isEditable && (
                                      <button
                                        type="button"
                                        onClick={() => handleAddSplit(line.id)}
                                        className="text-[10px] text-indigo-600 hover:text-indigo-800 hover:underline mt-0.5 text-right block w-full font-medium"
                                        title="Split this count across multiple shelves / locations / measures"
                                        data-testid={`count-split-init-${line.id}`}
                                      >
                                        + split count
                                      </button>
                                    )}
                                  </>
                                );
                              })()}
                              {convInfo && <p className="text-[10px] text-blue-600 font-medium mt-0.5 text-right">{convInfo}</p>}
                            </div>
                            <div className="w-8 flex items-center justify-center">
                              {isCounted ? (
                                <CheckCircle className="h-4 w-4 text-emerald-500" />
                              ) : (
                                <div className="h-4 w-4 rounded-full border-2 border-amber-300" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          {/* Observations — editable after import from tablet */}
          {importedObservations.length > 0 && (
            <Card className="border-blue-200 bg-blue-50/30" data-testid="imported-observations-panel">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <ClipboardList className="h-4 w-4 text-blue-600" />
                    Observations
                    <Badge variant="outline" className="text-[10px] border-blue-300 text-blue-700 bg-blue-100">
                      {importedObservations.length}
                    </Badge>
                  </CardTitle>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs"
                    onClick={() => setImportedObservations(prev => [...prev, {
                      local_id: `obs_new_${Date.now()}`,
                      note_text: '',
                      category: 'general',
                      severity: 'info',
                      photos: [],
                      created_at: new Date().toISOString(),
                      _new: true,
                    }])}
                    data-testid="add-observation-btn"
                  >
                    <Plus className="h-3 w-3 mr-1" /> Add Observation
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {importedObservations.map((obs, i) => (
                  <div key={obs.local_id || i} className="border rounded-lg p-3 bg-white" data-testid={`observation-${i}`}>
                    <div className="space-y-2">
                      <textarea
                        className="w-full text-sm border rounded-md p-2 min-h-[60px] resize-y focus:outline-none focus:ring-1 focus:ring-blue-300"
                        value={obs.note_text || ''}
                        placeholder="Observation notes..."
                        data-testid={`observation-text-${i}`}
                        onChange={(e) => {
                          const updated = [...importedObservations];
                          updated[i] = { ...updated[i], note_text: e.target.value };
                          setImportedObservations(updated);
                        }}
                      />
                      <div className="flex items-center gap-2 flex-wrap">
                        <select
                          className="text-xs border rounded px-2 py-1 bg-white"
                          value={obs.category || 'general'}
                          data-testid={`observation-category-${i}`}
                          onChange={(e) => {
                            const updated = [...importedObservations];
                            updated[i] = { ...updated[i], category: e.target.value };
                            setImportedObservations(updated);
                          }}
                        >
                          <option value="general">General</option>
                          <option value="damage">Damage</option>
                          <option value="expiry">Expiry</option>
                          <option value="storage">Storage</option>
                          <option value="discrepancy">Discrepancy</option>
                        </select>
                        <select
                          className="text-xs border rounded px-2 py-1 bg-white"
                          value={obs.severity || 'info'}
                          data-testid={`observation-severity-${i}`}
                          onChange={(e) => {
                            const updated = [...importedObservations];
                            updated[i] = { ...updated[i], severity: e.target.value };
                            setImportedObservations(updated);
                          }}
                        >
                          <option value="info">Info</option>
                          <option value="warning">Warning</option>
                          <option value="critical">Critical</option>
                        </select>
                        {obs.product_name_snapshot && <span className="text-[10px] text-muted-foreground">{obs.product_name_snapshot}</span>}
                        {obs.created_at && <span className="text-[10px] text-muted-foreground">{new Date(obs.created_at).toLocaleString()}</span>}
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0 ml-auto text-red-400 hover:text-red-600"
                          onClick={() => setImportedObservations(prev => prev.filter((_, idx) => idx !== i))}
                          data-testid={`observation-delete-${i}`}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </div>
                    </div>
                    {obs.photos && obs.photos.length > 0 && (
                      <div className="flex gap-2 mt-2 flex-wrap">
                        {obs.photos.map((photo, pi) => (
                          <div key={pi} className="relative group">
                            {photo.data_base64 ? (
                              <img
                                src={`data:${photo.mime_type || 'image/jpeg'};base64,${photo.data_base64}`}
                                alt={photo.caption || `Photo ${pi + 1}`}
                                className="h-16 w-16 rounded object-cover border cursor-pointer hover:opacity-80"
                                onClick={() => window.open(`data:${photo.mime_type || 'image/jpeg'};base64,${photo.data_base64}`, '_blank')}
                              />
                            ) : (
                              <div className="h-16 w-16 rounded border bg-slate-100 flex items-center justify-center text-[9px] text-slate-400">No data</div>
                            )}
                            <button
                              className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[8px] opacity-0 group-hover:opacity-100 transition-opacity"
                              onClick={() => {
                                const updated = [...importedObservations];
                                updated[i] = { ...updated[i], photos: updated[i].photos.filter((_, idx) => idx !== pi) };
                                setImportedObservations(updated);
                              }}
                            >&times;</button>
                            {photo.caption && <p className="text-[9px] text-muted-foreground mt-0.5 max-w-[64px] truncate">{photo.caption}</p>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}


          {/* Exception Lines Resolution — Added During Count */}
          {allExceptionLines.length > 0 && (
            <Card className={allUnresolvedExceptionLines.length > 0 ? 'border-amber-300 bg-amber-50/30' : 'border-emerald-200 bg-emerald-50/30'} data-testid="exception-lines-panel">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center gap-2">
                  <AlertTriangle className={`h-4 w-4 ${allUnresolvedExceptionLines.length > 0 ? 'text-amber-600' : 'text-emerald-600'}`} />
                  {nonAdcBlockedIds.size > 0 ? 'Cost Exception Lines' : isFirstSession ? (allUnresolvedExceptionLines.length > 0 ? 'Opening Inventory — Cost Required' : 'Opening Inventory — Costs Established') : 'Products Added During Count'}
                  <Badge variant="outline" className={`text-[10px] ${allUnresolvedExceptionLines.length > 0 ? 'border-amber-400 text-amber-700 bg-amber-100' : 'border-emerald-300 text-emerald-700 bg-emerald-100'}`}>
                    {preCalcLoading && !preCalcData ? 'Checking costs…' : allUnresolvedExceptionLines.length > 0 ? `${allUnresolvedExceptionLines.length} Needs Cost` : 'All Resolved'}
                  </Badge>
                </CardTitle>
                <CardDescription className="text-xs">
                  {allUnresolvedExceptionLines.length > 0
                    ? isFirstSession
                      ? 'This is the first audit at this site. Products counted for the first time need a cost per unit — this establishes the opening WAC basis for all future stocktakes.'
                      : nonAdcBlockedIds.size > 0
                        ? 'Some lines need a cost price before calculation can proceed — either no cost exists in the system, or the existing cost is zero or invalid. Enter the correct unit cost to resolve.'
                        : 'These products were added during counting and need a cost per unit before calculation can proceed. Enter the cost price per stock unit.'
                    : `${allExceptionLines.length - allUnresolvedExceptionLines.length} exception line${allExceptionLines.length !== 1 ? 's' : ''} resolved or exempt. Calculation can proceed.`
                  }
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {allExceptionLines.map(line => {
                    const entry = entries[line.id] || {};
                    const prod = productMap[line.beverage_product_id];
                    const prodUnits = prod?.units || [];
                    const serverCost = line.count_entry?.cost_price_override;
                    const localCost = exceptionCosts[line.id];
                    const saveError = exceptionCostErrors[line.id];
                    // >= 0: an explicit £0 (FOC) is a valid resolution.
                    // Previously `serverCost && serverCost > 0` treated 0 as
                    // falsy and kept FOC lines permanently amber.
                    const isResolved = serverCost !== null && serverCost !== undefined && serverCost >= 0 && !saveError;
                    const isExempt = !isResolved && !saveError && isServerExempt(line);
                    const currentCost = (localCost !== undefined && localCost !== '')
                      ? localCost
                      : (serverCost !== null && serverCost !== undefined ? String(serverCost) : '');
                    const isCounted = entry.quantity !== undefined && entry.quantity !== '';

                    return (
                      <div
                        key={line.id}
                        className={`flex items-center gap-3 py-2.5 px-3 rounded-lg border ${
                          saveError ? 'border-red-300 bg-red-50' :
                          isResolved ? 'border-emerald-200 bg-emerald-50/50' :
                          isExempt ? 'border-slate-200 bg-slate-50' : 'border-amber-300 bg-amber-50'
                        }`}
                        data-testid={`exception-line-${line.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <p className="text-sm font-medium truncate">{line.product_name_snapshot}</p>
                            {/* Line-type classification: distinguish ADC from existing-inventory lines */}
                            {line.added_during_count ? (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 border-orange-300 text-orange-700 bg-orange-50 shrink-0" title="This product was assigned to the site during the count — it needs a cost before the session can be calculated.">
                                New line
                              </Badge>
                            ) : nonAdcBlockedIds.has(line.id) ? (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 border-slate-300 text-slate-600 bg-slate-50 shrink-0" title="This is an existing inventory line with a nonzero count but no resolvable cost in the system.">
                                No system cost
                              </Badge>
                            ) : null}
                            {saveError ? (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 border-red-400 text-red-700 bg-red-100 shrink-0" data-testid={`badge-save-failed-${line.id}`}>
                                Save failed — retry
                              </Badge>
                            ) : isResolved ? (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 border-emerald-300 text-emerald-700 bg-emerald-100 shrink-0" data-testid={`badge-resolved-${line.id}`} title={line.count_entry?.cost_override_source === 'auto_wac' ? `Cost of £${Number(serverCost).toFixed(2)} was found automatically from this session's posted weighted-average cost — no manual entry needed.` : 'Cost entered manually.'}>
                                <CheckCircle className="h-2.5 w-2.5 mr-0.5" />{line.count_entry?.cost_override_source === 'auto_wac' ? `Auto: WAC £${Number(serverCost).toFixed(2)}` : 'Resolved'}
                              </Badge>
                            ) : isExempt ? (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 border-slate-300 text-slate-600 bg-white shrink-0" data-testid={`badge-no-cost-needed-${line.id}`} title={Number(entry.quantity) === 0 ? 'Counted 0 with no opening stock or posted receipts in the period — nothing to value' : 'A cost is available from posted transactions; Calculate will use it'}>
                                {Number(entry.quantity) === 0 ? 'No cost needed' : 'Cost available'}
                              </Badge>
                            ) : preCalcLoading ? (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 border-slate-300 text-slate-500 bg-white shrink-0" data-testid={`badge-checking-cost-${line.id}`}>
                                <Loader2 className="h-2.5 w-2.5 mr-0.5 animate-spin" />Checking cost…
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-[9px] px-1 py-0 border-red-300 text-red-700 bg-red-50 shrink-0" data-testid={`badge-requires-resolution-${line.id}`}
                                title={exceptionCostSourceMap.get(line.id) === 'missing' ? 'No cost exists for this product in the system — enter one manually.' : 'A cost was found but it is £0 or invalid — verify or enter the correct unit price.'}>
                                {exceptionCostSourceMap.get(line.id) === 'missing' ? 'No Cost Found' : 'Requires Resolution'}
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5">
                            {isCounted ? `${entry.quantity} × ${entry.unit_label || line.unit_label_snapshot || 'unit'}` : 'Not counted'}
                            {prodUnits.length > 0 && ` | Units: ${prodUnits.map(u => u.unit_type).join(', ')}`}
                          </p>
                          {saveError && (
                            <p className="text-[11px] text-red-700 mt-0.5" data-testid={`exception-cost-error-${line.id}`}>
                              {saveError}
                            </p>
                          )}
                        </div>
                        {isCounted && !isResolved && !isExempt && !(preCalcLoading && !preCalcData) && (
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="w-28">
                              <Input
                                type="number"
                                min="0"
                                step="0.01"
                                placeholder="Cost £ (0 for FOC)"
                                value={currentCost}
                                onChange={e => handleExceptionCostChange(line.id, e.target.value)}
                                onBlur={e => commitExceptionCost(line.id, e.currentTarget.value, entry.unit_label || line.unit_label_snapshot)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === 'Tab') {
                                    // iter595b — Prevent browser-native Tab focus-scroll.
                                    // Tab would move focus to the next DOM element, which
                                    // after the previous line resolves (DOM slot changes)
                                    // can be far from the exception panel, causing a ~3000px
                                    // scroll jump. preventDefault() blocks the native focus
                                    // move; explicit blur() still fires onBlur → commitExceptionCost.
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }
                                }}
                                className={`h-9 text-sm text-right tabular-nums ${saveError ? 'border-red-400' : ''}`}
                                disabled={!isEditable}
                                data-testid={`exception-cost-input-${line.id}`}
                              />
                            </div>
                            {resolvingLine === line.id && (
                              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                            )}
                          </div>
                        )}
                        {isCounted && isResolved && (
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="w-28">
                              <Input
                                type="number"
                                min="0.01"
                                step="0.01"
                                value={currentCost}
                                onChange={e => handleExceptionCostChange(line.id, e.target.value)}
                                onBlur={e => commitExceptionCost(line.id, e.currentTarget.value, entry.unit_label || line.unit_label_snapshot)}
                                onKeyDown={e => {
                                  if (e.key === 'Enter' || e.key === 'Tab') {
                                    e.preventDefault();
                                    e.currentTarget.blur();
                                  }
                                }}
                                className="h-9 text-sm text-right tabular-nums border-emerald-200"
                                disabled={!isEditable}
                                data-testid={`exception-cost-input-${line.id}`}
                              />
                            </div>
                            {resolvingLine === line.id && (
                              <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Field Observations — from AbbeyZone Count */}
          <Card data-testid="closing-observations-panel">
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center gap-2">
                <Camera className="h-4 w-4 text-slate-600" />
                Field Observations
                {observations.length > 0 && (
                  <Badge variant="secondary" className="text-[10px]">{observations.length}</Badge>
                )}
              </CardTitle>
              <CardDescription className="text-xs">
                Observations and photos captured during counting in AbbeyZone Count.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {observationsLoading ? (
                <div className="py-6 text-center text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin inline mr-2" />Loading observations...</div>
              ) : observations.length === 0 ? (
                <div className="py-6 text-center text-muted-foreground" data-testid="closing-observations-empty">
                  <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  <p className="text-sm">No observations recorded</p>
                  <p className="text-xs mt-1">Use AbbeyZone Count to capture notes and photos during field counting.</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="closing-observations-list">
                  {observations.map(obs => {
                    const severityColors = {
                      info: 'bg-slate-50 text-slate-700 border-slate-200',
                      low: 'bg-blue-50 text-blue-700 border-blue-200',
                      medium: 'bg-amber-50 text-amber-700 border-amber-200',
                      high: 'bg-orange-50 text-orange-700 border-orange-200',
                      critical: 'bg-red-50 text-red-700 border-red-200',
                    };
                    const isEditing = editingObsId === obs.id;

                    return (
                      <div
                        key={obs.id || obs.local_id}
                        className="border border-slate-100 rounded-lg p-3"
                        data-testid={`observation-item-${obs.id || obs.local_id}`}
                      >
                        {isEditing ? (
                          /* ── Inline edit mode ── */
                          <div className="space-y-2" data-testid={`observation-edit-${obs.id}`}>
                            <textarea
                              value={obsEditForm.note_text}
                              onChange={e => setObsEditForm(f => ({ ...f, note_text: e.target.value }))}
                              className="w-full text-sm border border-slate-200 rounded-lg p-2 resize-none focus:ring-1 focus:ring-slate-300"
                              rows={2}
                              data-testid={`observation-edit-note-${obs.id}`}
                            />
                            <div className="flex gap-2">
                              <select
                                value={obsEditForm.category}
                                onChange={e => setObsEditForm(f => ({ ...f, category: e.target.value }))}
                                className="flex-1 h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white"
                                data-testid={`observation-edit-category-${obs.id}`}
                              >
                                {['general','damage','expiry','discrepancy','hygiene','security','other'].map(c => (
                                  <option key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</option>
                                ))}
                              </select>
                              <select
                                value={obsEditForm.severity}
                                onChange={e => setObsEditForm(f => ({ ...f, severity: e.target.value }))}
                                className="flex-1 h-8 px-2 text-xs border border-slate-200 rounded-lg bg-white"
                                data-testid={`observation-edit-severity-${obs.id}`}
                              >
                                {['info','low','medium','high','critical'].map(s => (
                                  <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>
                                ))}
                              </select>
                            </div>
                            <div className="flex gap-2 justify-end">
                              <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setEditingObsId(null)} data-testid={`observation-cancel-edit-${obs.id}`}>Cancel</Button>
                              <Button size="sm" className="h-7 text-xs" onClick={() => handleSaveEditObs(obs.id)} data-testid={`observation-save-edit-${obs.id}`}>Save</Button>
                            </div>
                          </div>
                        ) : (
                          /* ── Read mode ── */
                          <>
                            <div className="flex items-start justify-between gap-2 mb-1.5">
                              <div className="flex items-center gap-1.5 flex-wrap">
                                {obs.category && (
                                  <Badge variant="outline" className="text-[10px] px-1.5 py-0 capitalize">{obs.category}</Badge>
                                )}
                                {obs.severity && (
                                  <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${severityColors[obs.severity] || ''}`}>
                                    {obs.severity}
                                  </Badge>
                                )}
                              </div>
                              <div className="flex items-center gap-1 shrink-0">
                                <span className="text-[10px] text-muted-foreground mr-1">
                                  {obs.created_at && format(new Date(obs.created_at), 'dd MMM HH:mm')}
                                </span>
                                {isEditable && (
                                  <>
                                    <button
                                      onClick={() => handleStartEditObs(obs)}
                                      className="p-1 text-slate-400 hover:text-slate-600 transition-colors"
                                      title="Edit observation"
                                      data-testid={`observation-edit-btn-${obs.id}`}
                                    >
                                      <Pencil className="w-3.5 h-3.5" />
                                    </button>
                                    <button
                                      onClick={() => handleDeleteObservation(obs.id)}
                                      className="p-1 text-slate-400 hover:text-red-500 transition-colors"
                                      title="Delete observation"
                                      data-testid={`observation-delete-btn-${obs.id}`}
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                            <p className="text-sm text-slate-800">{obs.note_text}</p>
                            {(obs.product_name_snapshot || obs.location_name_snapshot) && (
                              <p className="text-xs text-slate-400 mt-1">
                                {obs.product_name_snapshot}{obs.product_name_snapshot && obs.location_name_snapshot && ' · '}{obs.location_name_snapshot}
                              </p>
                            )}
                            {obs.photos && obs.photos.length > 0 && (
                              <div className="flex gap-2 mt-2" data-testid={`observation-photos-${obs.id}`}>
                                {obs.photos.map(photo => (
                                  <button
                                    key={photo.id}
                                    onClick={() => setLightboxSrc(photo.storage_url)}
                                    className="relative group cursor-pointer"
                                    data-testid={`observation-photo-${photo.id}`}
                                  >
                                    <img
                                      src={photo.storage_url}
                                      alt={photo.caption || 'Observation photo'}
                                      className="w-24 h-24 object-cover rounded-lg border border-slate-200 group-hover:border-cyan-400 transition-colors"
                                    />
                                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 rounded-lg transition-colors flex items-center justify-center">
                                      <ZoomIn className="w-4 h-4 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                                    </div>
                                    {photo.caption && (
                                      <p className="text-[9px] text-slate-500 mt-0.5 truncate max-w-[96px]">{photo.caption}</p>
                                    )}
                                  </button>
                                ))}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Photo lightbox */}
          {lightboxSrc && (
            <div
              className="fixed inset-0 z-[70] bg-black/90 flex items-center justify-center p-4 animate-in fade-in duration-200"
              onClick={() => setLightboxSrc(null)}
              data-testid="wizard-photo-lightbox"
            >
              <button
                onClick={() => setLightboxSrc(null)}
                className="absolute top-4 right-4 p-2 bg-white/10 rounded-full text-white hover:bg-white/20 transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={lightboxSrc}
                alt="Full size"
                className="max-w-full max-h-[85vh] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
        </div>
      )}

      {/* iter345 — Step: Previous YTD */}
      {currentStepDef.key === 'previous_ytd' && (
        <PreviousYTDPanel
          sessionId={session.id}
          editable={isEditable}
          onAdvance={() => setWizardStep(s => Math.min(s + 1, steps.length - 1))}
        />
      )}

      {/* Step: Calculate & Review */}
      {currentStepDef.key === 'calculate' && (
        <div className="space-y-4">
          {isOfflineMode ? (
            /* ── Offline Calculation Panel ── */
            <>
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-amber-600" />
                  Local Calculation
                  <Badge className="bg-amber-100 text-amber-700 text-[10px]"><WifiOff className="h-3 w-3 mr-0.5" />Offline</Badge>
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-mono border bg-purple-100 text-purple-700 border-purple-200" data-testid="data-source-local-engine">Local Engine</span>
                </CardTitle>
                <CardDescription>
                  Calculation runs locally on your device using the same audit rules as the server.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4">
                  <Button size="sm" variant="outline" onClick={handleLocalCalculate} disabled={calculating || countedLines === 0} data-testid="offline-calculate-btn">
                    {calculating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Calculator className="h-3 w-3 mr-1" />}
                    {offlineCalcResult ? 'Recalculate' : 'Calculate'}
                  </Button>
                  {countedLines === 0 && (
                    <span className="text-xs text-red-600 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" /> No count entries — go back to Closing Stock
                    </span>
                  )}
                </div>
                    {offlineCalcResult && (
                  <div className="space-y-4">
                    {/* Phase 4 — methodology-aware headline (offline mirror) */}
                    {(() => {
                      const methodology = offlineCalcResult.headline_methodology || 'legacy';
                      const isRevised = methodology === 'revised';
                      const blocker = (offlineCalcResult.calculation_warnings || []).find(w => w.code === 'accounts_sales_required_offline');
                      return (
                        <>
                          {blocker && (
                            <div className="rounded-lg border border-red-300 bg-red-50 p-3" data-testid="accounts-sales-required-banner-offline">
                              <div className="flex items-start gap-2">
                                <AlertTriangle className="h-4 w-4 text-red-600 shrink-0 mt-0.5" />
                                <div className="text-xs text-red-800">
                                  <p className="font-semibold">Actual Receipts required</p>
                                  <p className="mt-0.5">{blocker.message}</p>
                                  <Button size="sm" variant="outline" className="mt-2 h-7 text-xs" onClick={() => handleStepClick(steps.findIndex(s => s.key === 'sales'))} data-testid="goto-sales-step-btn">
                                    Go to Sales step
                                  </Button>
                                </div>
                              </div>
                            </div>
                          )}
                          <div className="flex flex-wrap items-center gap-2 text-[11px]">
                            <Badge
                              variant="outline"
                              className={`font-medium ${isRevised ? 'border-emerald-400 bg-emerald-50 text-emerald-700' : 'border-slate-300 bg-slate-50 text-slate-600'}`}
                              data-testid="headline-methodology-badge-offline"
                            >
                              {isRevised ? 'Actual Receipts − Est. Receipts + Allow − Mgmt' : 'Legacy calculation method'}
                            </Badge>
                            <span className="text-muted-foreground">
                              {isRevised
                                ? 'Headline result = Actual Receipts (incl VAT) − Estimated Receipts (incl VAT).'
                                : 'Headline result = Actual Closing − Expected Closing.'}
                            </span>
                          </div>
                          {isRevised && !blocker && (
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg text-center" data-testid="offline-revised-accounts-sales">
                                <p className="text-xs text-muted-foreground">Actual Receipts (incl VAT)</p>
                                <p className="text-base font-bold text-indigo-700">{formatCurrency(offlineCalcResult.accounts_sales_incl_vat ?? offlineCalcResult.accounts_sales_excl_vat)}</p>
                              </div>
                              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-center" data-testid="offline-revised-estimated-receipts">
                                <p className="text-xs text-muted-foreground">- Estimated Receipts (incl VAT)</p>
                                <p className="text-base font-bold text-amber-700">{formatCurrency(offlineCalcResult.estimated_receipts_incl_vat ?? offlineCalcResult.estimated_receipts_excl_vat)}</p>
                              </div>
                              <div className={`p-3 border rounded-lg text-center ${(offlineCalcResult.headline_stocktake_result ?? 0) >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`} data-testid="offline-revised-headline-result">
                                <p className="text-xs text-muted-foreground">= Result</p>
                                <p className={`text-base font-bold ${(offlineCalcResult.headline_stocktake_result ?? 0) >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                                  {formatCurrency(offlineCalcResult.headline_stocktake_result)}
                                </p>
                              </div>
                            </div>
                          )}
                          {isRevised && (
                            <div className="p-2 rounded-md bg-slate-50 border border-slate-200 text-[11px] text-slate-700" data-testid="offline-cos-indirect">
                              <span className="font-semibold">Cost of Sales (indirect — stock-movement):</span>{' '}
                              <span className="tabular-nums">{formatCurrency(offlineCalcResult.cost_of_sales_indirect)}</span>
                              {offlineCalcResult.headline_gross_profit != null && (
                                <>
                                  <span className="mx-2 text-slate-400">|</span>
                                  <span className="font-semibold">Gross Profit:</span>{' '}
                                  <span className="tabular-nums">{formatCurrency(offlineCalcResult.headline_gross_profit)}</span>
                                  {offlineCalcResult.headline_gp_pct != null && (
                                    <span className="ml-1 text-muted-foreground">({offlineCalcResult.headline_gp_pct}%)</span>
                                  )}
                                </>
                              )}
                            </div>
                          )}
                        </>
                      );
                    })()}
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div className="p-3 bg-slate-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Opening Stock</p>
                        <p className="text-base font-bold">{formatCurrency(offlineCalcResult.opening_stock_value)}</p>
                      </div>
                      <div className="p-3 bg-green-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">+ Deliveries</p>
                        <p className="text-base font-bold text-green-700">{formatCurrency(offlineCalcResult.deliveries_value)}</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">- Credits</p>
                        <p className="text-base font-bold text-red-700">{formatCurrency(offlineCalcResult.credits_value)}</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">- Cost of Sales</p>
                        <p className="text-base font-bold text-red-700">{formatCurrency(offlineCalcResult.sales_value)}</p>
                      </div>
                      <div className="p-3 bg-red-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">- Adjustments</p>
                        <p className="text-base font-bold text-red-700">{formatCurrency(offlineCalcResult.adjustments_value)}</p>
                      </div>
                      <div className="p-3 bg-blue-50 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Expected Closing</p>
                        <p className="text-base font-bold text-blue-700">{formatCurrency(offlineCalcResult.expected_closing_value)}</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-center">
                        <p className="text-xs text-muted-foreground">Actual Closing Stock</p>
                        <p className="text-xl font-bold text-emerald-700">{formatCurrency(offlineCalcResult.total_value)}</p>
                        <p className="text-xs text-muted-foreground">{offlineCalcResult.line_count} lines counted</p>
                      </div>
                      <div className={`p-3 border rounded-lg text-center ${offlineCalcResult.stocktake_result >= 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                        <p className="text-xs text-muted-foreground">Result {(offlineCalcResult.headline_methodology || 'legacy') === 'revised' ? '(Legacy — reference)' : ''}</p>
                        <p className={`text-xl font-bold ${offlineCalcResult.stocktake_result >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {formatCurrency(offlineCalcResult.stocktake_result)}
                        </p>
                        <p className="text-xs text-muted-foreground">Actual - Expected</p>
                      </div>
                    </div>

                    {/* 2026-04-24 iter313 — split Adjustments into Management Cost of Sales + Retail inc VAT sub-row + Allowance.
                        2026-05-07 iter333 — Management retail tile flipped to INC VAT. */}
                    {(() => {
                      const mgmtB = offlineCalcResult.management_value || 0;
                      const mgmtIncl = offlineCalcResult.management_sales_incl_vat || 0;
                      const allow = offlineCalcResult.allowance_value || 0;
                      const cosPct = offlineCalcResult.management_cos_budget_pct;
                      if (!mgmtIncl && !allow) return null;
                      return (
                        <div className="rounded-lg border border-slate-200 bg-slate-50/40 p-3 space-y-1.5" data-testid="offline-adjustments-split">
                          <p className="text-[11px] uppercase tracking-wide text-slate-500 font-semibold">Adjustments split</p>
                          {mgmtIncl !== 0 && (
                            <>
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-700">Management retail (inc VAT)</span>
                                <span className="font-semibold tabular-nums">{formatCurrency(mgmtIncl)}</span>
                              </div>
                              <div className="flex justify-between items-center text-[11px] text-muted-foreground pl-4">
                                <span>
                                  +/- Management Cost of Sales (part of COS)
                                  {cosPct != null && <span className="ml-1">· COS budget {Number(cosPct).toFixed(2)}%</span>}
                                </span>
                                <span className="tabular-nums">{formatCurrency(mgmtB)}</span>
                              </div>
                            </>
                          )}
                          {allow !== 0 && (
                            <div className="flex justify-between items-center text-xs">
                              <span className="text-slate-700">Allowances (excluded from COS, applied to final result)</span>
                              <span className="font-semibold tabular-nums">{formatCurrency(allow)}</span>
                            </div>
                          )}
                        </div>
                      );
                    })()}

                    {/* 2026-04-24 iter313 — Raw → −Allowances → −Mgmt retail → = Final adjusted result block
                        2026-05-07 iter333 — Mgmt retail subtraction now INC VAT. */}
                    {(() => {
                      const methodology = offlineCalcResult.headline_methodology || 'legacy';
                      const isRevised = methodology === 'revised';
                      const rawRevised = offlineCalcResult.stocktake_result_revised;
                      const rawLegacy = offlineCalcResult.stocktake_result_legacy ?? offlineCalcResult.stocktake_result;
                      const adjustedRevised = offlineCalcResult.stocktake_result_revised_adjusted;
                      const adjustedLegacy = offlineCalcResult.stocktake_result_legacy_adjusted ?? rawLegacy;
                      const headlineRaw = isRevised ? rawRevised : rawLegacy;
                      const finalResult = isRevised
                        ? (adjustedRevised ?? offlineCalcResult.headline_stocktake_result ?? rawRevised)
                        : adjustedLegacy;
                      const allow = offlineCalcResult.allowance_value || 0;
                      const mgmtIncl = offlineCalcResult.management_sales_incl_vat || 0;
                      const hasAllow = allow !== 0 && headlineRaw != null;
                      const hasMgmt = mgmtIncl !== 0 && headlineRaw != null;
                      if (!hasAllow && !hasMgmt) return null;
                      const cols = 2 + (hasAllow ? 1 : 0) + (hasMgmt ? 1 : 0);
                      const gridCls = cols === 4 ? 'grid-cols-4' : 'grid-cols-3';
                      return (
                        <div className={`grid ${gridCls} gap-3 text-xs`} data-testid="offline-allowance-adjustment-block">
                          <div className="p-2 bg-slate-50 rounded border border-slate-200">
                            <span className="text-slate-500">Raw {isRevised ? 'Revised' : 'Legacy'} Result</span>
                            <span className="font-semibold tabular-nums text-slate-800 block" data-testid="offline-headline-raw-result">
                              {formatCurrency(headlineRaw)}
                            </span>
                            <span className="text-[10px] text-muted-foreground">before adjustments</span>
                          </div>
                          {hasAllow && (
                            <div className="p-2 bg-indigo-50 rounded border border-indigo-200">
                              <span className="text-indigo-600">− Allowances (direct adj.)</span>
                              <span className="font-semibold tabular-nums text-indigo-700 block" data-testid="offline-headline-allowance-adjustment">
                                {formatCurrency(-allow)}
                              </span>
                              <span className="text-[10px] text-muted-foreground">excluded from COS / GP</span>
                            </div>
                          )}
                          {hasMgmt && (
                            <div className="p-2 bg-teal-50 rounded border border-teal-200">
                              <span className="text-teal-700">+ Mgmt Retail (inc VAT)</span>
                              <span className="font-semibold tabular-nums text-teal-800 block" data-testid="offline-headline-management-retail-adjustment">
                                {formatCurrency(mgmtIncl)}
                              </span>
                              <span className="text-[10px] text-muted-foreground">retail diverted to Mgmt</span>
                            </div>
                          )}
                          <div className={`p-2 rounded border ${(finalResult ?? 0) > 0 ? 'bg-emerald-50 border-emerald-200' : (finalResult ?? 0) < 0 ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
                            <span className="text-slate-500">= Final Result</span>
                            <span className={`font-semibold tabular-nums block ${(finalResult ?? 0) > 0 ? 'text-emerald-700' : (finalResult ?? 0) < 0 ? 'text-red-700' : 'text-slate-800'}`} data-testid="offline-headline-final-result">
                              {formatCurrency(finalResult)}
                            </span>
                            <span className="text-[10px] text-muted-foreground">after adjustments</span>
                          </div>
                        </div>
                      );
                    })()}

                    {/* 2026-05-07 iter334 — Yield % KPI: denominator ADDS Management (inc VAT). */}
                    {offlineCalcResult.yield_pct != null && (() => {
                      const yp = offlineCalcResult.yield_pct;
                      const band = Math.abs(yp - 100);
                      const color = band <= 2 ? 'text-emerald-600' : band <= 10 ? 'text-amber-600' : 'text-red-600';
                      const estR = offlineCalcResult.estimated_receipts_incl_vat ?? offlineCalcResult.estimated_receipts_excl_vat ?? offlineCalcResult.sales_at_selling;
                      const actR = offlineCalcResult.accounts_sales_incl_vat ?? offlineCalcResult.accounts_sales_excl_vat;
                      const mgmt = offlineCalcResult.management_sales_incl_vat || 0;
                      const denom = (estR != null) ? Number(estR) + Number(mgmt) : null;
                      return (
                        <div className="rounded-lg border border-slate-200 bg-white p-3 flex items-center justify-between" data-testid="offline-yield-kpi">
                          <div>
                            <p className="text-[11px] text-muted-foreground uppercase tracking-wide">Yield %</p>
                            <p className={`text-lg font-bold ${color} tabular-nums`}>{yp.toFixed(1)}%</p>
                            <p className="text-[11px] text-muted-foreground">Actual Receipts / (Estimated Receipts + Management) (incl VAT) × 100</p>
                          </div>
                          <div className="text-right text-[11px] text-muted-foreground">
                            <p>Actual Receipts: <span className="tabular-nums">{actR != null ? formatCurrency(actR) : '—'}</span></p>
                            <p>Estimated Receipts: <span className="tabular-nums">{formatCurrency(estR)}</span></p>
                            {Number(mgmt) !== 0 && (
                              <p>+ Management (inc VAT): <span className="tabular-nums">{formatCurrency(mgmt)}</span></p>
                            )}
                            {denom != null && (
                              <p className="text-slate-700">= Target: <span className="tabular-nums">{formatCurrency(denom)}</span></p>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    {/* Per-line breakdown */}
                    {offlineCalcResult.lines && offlineCalcResult.lines.length > 0 && (
                      <Card>
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm">Line Breakdown</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-1.5">
                            {offlineCalcResult.lines.map((ln, i) => (
                              <div key={i} className="flex items-center justify-between py-1.5 border-b border-slate-100 last:border-0 text-xs">
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium truncate">{ln.product_name_snapshot}</p>
                                  <p className="text-muted-foreground">
                                    {ln.count_quantity} {ln.count_unit_label || ''} = {ln.base_quantity} {ln.base_unit} @ {formatCurrency(ln.cost_per_base_unit)}
                                  </p>
                                </div>
                                <div className="text-right flex-shrink-0 ml-2">
                                  <p className="font-bold">{formatCurrency(ln.line_value)}</p>
                                  <p className={`${ln.variance_value >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                    Var: {formatCurrency(ln.variance_value)}
                                  </p>
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Stock Audit Analytics (Movement Trail) — Collapsible */}
                    {offlineCalcResult.lines && offlineCalcResult.lines.length > 0 && (
                      <Collapsible open={offlineMovementOpen} onOpenChange={setOfflineMovementOpen}>
                        <CollapsibleTrigger asChild>
                          <Button variant="outline" className="w-full justify-between text-sm font-semibold" data-testid="offline-movement-trail-toggle">
                            Stock Audit Analytics
                            <ChevronRight className={`h-4 w-4 transition-transform ${offlineMovementOpen ? 'rotate-90' : ''}`} />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <Card className="mt-2">
                            <CardContent className="pt-4">
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b text-left text-muted-foreground">
                                      <th className="py-1.5 pr-2">Product</th>
                                      <th className="py-1.5 px-2 text-right">Expected Value</th>
                                      <th className="py-1.5 px-2 text-right">Actual Value</th>
                                      <th className="py-1.5 px-2 text-right">Variance</th>
                                      <th className="py-1.5 px-2 text-right">Cost Source</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {offlineCalcResult.lines.filter(l => l.count_quantity > 0 || (l.expected_value || 0) !== 0).map((ln, i) => (
                                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                                        <td className="py-1.5 pr-2 font-medium truncate max-w-[160px]">{ln.product_name_snapshot}</td>
                                        <td className="py-1.5 px-2 text-right tabular-nums">{formatCurrency(ln.expected_value)}</td>
                                        <td className="py-1.5 px-2 text-right tabular-nums">{formatCurrency(ln.line_value)}</td>
                                        <td className={`py-1.5 px-2 text-right tabular-nums font-medium ${(ln.variance_value || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                          {formatCurrency(ln.variance_value)}
                                        </td>
                                        <td className="py-1.5 px-2 text-right text-muted-foreground">{(ln.cost_source || '').replace(/_/g, ' ')}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="border-t-2 font-semibold">
                                      <td className="py-2 pr-2">Total</td>
                                      <td className="py-2 px-2 text-right">{formatCurrency(offlineCalcResult.expected_closing_value)}</td>
                                      <td className="py-2 px-2 text-right">{formatCurrency(offlineCalcResult.total_value)}</td>
                                      <td className={`py-2 px-2 text-right ${offlineCalcResult.stocktake_result >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {formatCurrency(offlineCalcResult.stocktake_result)}
                                      </td>
                                      <td></td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </CardContent>
                          </Card>
                        </CollapsibleContent>
                      </Collapsible>
                    )}

                    {/* Product Analytics — Collapsible */}
                    {offlineCalcResult.lines && offlineCalcResult.lines.length > 0 && (
                      <Collapsible open={offlineProductOpen} onOpenChange={setOfflineProductOpen}>
                        <CollapsibleTrigger asChild>
                          <Button variant="outline" className="w-full justify-between text-sm font-semibold" data-testid="offline-product-analytics-toggle">
                            Product Analysis
                            <ChevronRight className={`h-4 w-4 transition-transform ${offlineProductOpen ? 'rotate-90' : ''}`} />
                          </Button>
                        </CollapsibleTrigger>
                        <CollapsibleContent>
                          <Card className="mt-2">
                            <CardContent className="pt-4">
                              <div className="overflow-x-auto">
                                <table className="w-full text-xs">
                                  <thead>
                                    <tr className="border-b text-left text-muted-foreground">
                                      <th className="py-1.5 pr-2">Product</th>
                                      <th className="py-1.5 px-2 text-right">Count Qty</th>
                                      <th className="py-1.5 px-2 text-right">Unit</th>
                                      <th className="py-1.5 px-2 text-right">Stock Qty</th>
                                      <th className="py-1.5 px-2 text-right">Cost/Unit</th>
                                      <th className="py-1.5 px-2 text-right">Value</th>
                                      <th className="py-1.5 px-2 text-right">Expected Qty</th>
                                      <th className="py-1.5 px-2 text-right">Variance Qty</th>
                                      <th className="py-1.5 px-2 text-right">Variance Value</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {offlineCalcResult.lines.filter(l => l.count_quantity > 0).map((ln, i) => (
                                      <tr key={i} className="border-b border-slate-50 hover:bg-slate-50/50">
                                        <td className="py-1.5 pr-2 font-medium truncate max-w-[160px]">{ln.product_name_snapshot}</td>
                                        <td className="py-1.5 px-2 text-right tabular-nums">{ln.count_quantity}</td>
                                        <td className="py-1.5 px-2 text-right">{ln.count_unit_label || '-'}</td>
                                        <td className="py-1.5 px-2 text-right tabular-nums">{ln.base_quantity?.toFixed(4)}</td>
                                        <td className="py-1.5 px-2 text-right tabular-nums">{formatCurrency(ln.cost_per_base_unit)}</td>
                                        <td className="py-1.5 px-2 text-right tabular-nums font-medium">{formatCurrency(ln.line_value)}</td>
                                        <td className="py-1.5 px-2 text-right tabular-nums">{ln.expected_qty?.toFixed(4)}</td>
                                        <td className={`py-1.5 px-2 text-right tabular-nums ${(ln.variance_qty || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                          {ln.variance_qty?.toFixed(4)}
                                        </td>
                                        <td className={`py-1.5 px-2 text-right tabular-nums font-medium ${(ln.variance_value || 0) >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                          {formatCurrency(ln.variance_value)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                  <tfoot>
                                    <tr className="border-t-2 font-semibold">
                                      <td className="py-2 pr-2">Totals</td>
                                      <td className="py-2 px-2 text-right">{offlineCalcResult.lines.filter(l => l.count_quantity > 0).reduce((s, l) => s + l.count_quantity, 0)}</td>
                                      <td colSpan="3"></td>
                                      <td className="py-2 px-2 text-right">{formatCurrency(offlineCalcResult.total_value)}</td>
                                      <td></td>
                                      <td></td>
                                      <td className={`py-2 px-2 text-right ${offlineCalcResult.stocktake_result >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                        {formatCurrency(offlineCalcResult.stocktake_result)}
                                      </td>
                                    </tr>
                                  </tfoot>
                                </table>
                              </div>
                            </CardContent>
                          </Card>
                        </CollapsibleContent>
                      </Collapsible>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
            <ValidationReportPanel
              sessionId={session.id}
              validationReport={validationReport}
              onRefresh={setValidationReport}
              isOfflineMode={true}
              onRunLocalValidation={handleRunLocalValidation}
            />
            </>
          ) : isStockAudit ? (
            <>
              {/* Block calculation if unresolved exception lines exist (ADC or non-ADC) */}
              {allUnresolvedExceptionLines.length > 0 && (
                <Card className="border-red-300 bg-red-50/50" data-testid="exception-block-banner">
                  <CardContent className="pt-4 pb-3">
                    <div className="flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-red-900">
                          {allUnresolvedExceptionLines.length} exception line{allUnresolvedExceptionLines.length !== 1 ? 's' : ''} require resolution
                        </p>
                        <p className="text-xs text-red-700 mt-0.5">
                          {unresolvedNonAdcLines.length > 0 && unresolvedExceptionLines.length === 0
                            ? 'Some inventory lines have a nonzero count but no resolvable cost — enter the unit cost to proceed.'
                            : unresolvedNonAdcLines.length > 0
                              ? 'Some exception lines (including products added during counting and existing inventory) need a cost price before calculation can proceed.'
                              : 'Products added during counting need a cost price before the calculation can proceed.'}
                          {' '}Go back to <button className="underline font-medium" onClick={() => handleStepClick(steps.findIndex(s => s.key === 'closing'))}>Closing Stock</button> to resolve.
                        </p>
                        <div className="mt-2 space-y-1">
                          {allUnresolvedExceptionLines.map(l => (
                            <div key={l.id} className="text-xs text-red-800 flex items-center gap-1.5">
                              <span className="w-1.5 h-1.5 rounded-full bg-red-400 shrink-0" />
                              {l.product_name_snapshot} — {entries[l.id]?.quantity || 0} {entries[l.id]?.unit_label || l.unit_label_snapshot || 'units'}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
              <StockAuditResultsPanel
                sessionId={session.id}
                status={session.status}
                countedLines={countedLines}
                dirty={dirty}
                onSave={handleSave}
                onValidationUpdate={setValidationReport}
                isCellarSession={isCellarSession}
                isLocationSession={isLocationSession}
                hasCellar={session?.audit_mode === 'multi_with_cellar'}
                isReadOnly={session.is_psb_historical === true}
                costBlocked={allUnresolvedExceptionLines.length > 0}
              />
              <ValidationReportPanel
                sessionId={session.id}
                validationReport={validationReport}
                onRefresh={setValidationReport}
              />
            </>
          ) : (
            /* Valuation calculation */
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Calculator className="h-5 w-5 text-emerald-600" />
                  Closing Stock Valuation
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2 mb-4">
                  {/* Wave 5A P6: Calculate is hidden for PSB historical — the snapshot is permanent */}
                  {!session.is_psb_historical && (
                  <Button size="sm" variant="outline" onClick={handleCalculate} disabled={calculating || countedLines === 0 || allUnresolvedExceptionLines.length > 0} data-testid="recalculate-btn">
                    {calculating ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <Calculator className="h-3 w-3 mr-1" />}
                    {calculation ? 'Recalculate' : 'Calculate'}
                  </Button>
                  )}
                </div>
                {calculation && (
                  <div className="space-y-3">
                    <div className="grid grid-cols-3 gap-3 text-center">
                      <div><p className="text-xs text-muted-foreground">Lines</p><p className="text-lg font-bold">{calculation.line_count}</p></div>
                      <div><p className="text-xs text-muted-foreground">Total Value</p><p className="text-lg font-bold text-emerald-700">{formatCurrency(calculation.total_value)}</p></div>
                      <div><p className="text-xs text-muted-foreground">Version</p><p className="text-lg font-bold">{calculation.version}</p></div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Publish/Lock section — only when online */}
          {isEditable && !isOfflineMode && (
            <Card className="border-emerald-200 bg-emerald-50/30">
              <CardContent className="pt-5 pb-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-emerald-800">Ready to publish?</p>
                    <p className="text-xs text-emerald-600 mt-0.5">
                      Locking this session will finalise the stocktake. Opening stock for the next session
                      will carry forward from this one.
                    </p>
                  </div>
                  <Button
                    onClick={() => handlePublish()}
                    disabled={publishing || countedLines === 0}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="publish-btn"
                  >
                    {publishing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Send className="h-4 w-4 mr-1" />}
                    Save & Publish
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* ══ UNIFIED BOTTOM NAVIGATION ══ */}
      <Card>
        <CardContent className="pt-4 pb-3">
          <div className="flex items-center justify-between">
            <Button
              variant="outline"
              onClick={handlePrevStep}
              disabled={wizardStep === 0}
              data-testid="prev-step-btn"
            >
              <ChevronLeft className="h-4 w-4 mr-1" />Previous
            </Button>
            <div className="flex items-center gap-3">
              {/* Wave 5A P6: suppress "Unsaved changes" for PSB historical (read-only, never dirty) */}
              {dirty && !session.is_psb_historical && (
                <span className="text-xs text-amber-600 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Unsaved changes
                </span>
              )}
              {/* Wave 5A P6: show read-only label for PSB historical sessions */}
              {session.is_psb_historical && (
                <span className="text-xs text-teal-600 flex items-center gap-1" data-testid="psb-readonly-nav-label">
                  <Lock className="h-3 w-3" /> View only
                </span>
              )}
              <span className="text-xs text-muted-foreground">
                Step {wizardStep + 1} of {steps.length}
              </span>
            </div>
            {wizardStep < steps.length - 1 ? (
              /* Wave 5A P6: PSB historical — "Next" only (no save). Regular sessions — "Save & Next". */
              session.is_psb_historical ? (
                <Button onClick={handleNextStep} data-testid="next-step-btn">
                  Next<ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              ) : (
                <Button onClick={handleNextStep} disabled={saving} data-testid="next-step-btn">
                  {saving ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  Save &amp; Next<ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )
            ) : (
              /* Wave 5A P6: PSB historical — no Save Draft; regular sessions — Save Draft. */
              !session.is_psb_historical && (
                <Button
                  variant="outline"
                  onClick={() => handleSave(true)}
                  disabled={saving}
                  data-testid="save-draft-btn"
                >
                  <Save className="h-4 w-4 mr-1" />{saving ? 'Saving...' : 'Save Draft'}
                </Button>
              )
            )}
          </div>
        </CardContent>
      </Card>
      </>
      )}
      <AddFromMasterModal
        open={addFromMasterOpen}
        onOpenChange={setAddFromMasterOpen}
        siteId={session?.site_id || null}
        assignedProductIds={lines.map(l => l.beverage_product_id).filter(Boolean)}
        onAssigned={() => loadSession()}
        offlineProducts={isOfflineMode ? (offlinePkg?.package?.products || []) : null}
        onOfflineAssign={isOfflineMode ? handleOfflineAssignFromMaster : null}
      />
      <SiteInventorySetupModal
        open={siteInventorySetupOpen}
        onOpenChange={setSiteInventorySetupOpen}
        siteId={session?.site_id || null}
        siteName={session?.site_name || ''}
        isOfflineMode={isOfflineMode}
        offlineData={isOfflineMode ? (offlinePkg?.package || null) : null}
        onOfflineMasterAssign={isOfflineMode ? handleOfflineAssignFromMaster : null}
        onOfflineMeasureChange={isOfflineMode ? handleOfflineMeasureChange : null}
        onOnlineRefresh={() => loadSession()}
      />

      {/* Report Centre dialog */}
      <StocktakeReportsDialog
        open={reportsDialogOpen}
        onOpenChange={setReportsDialogOpen}
        sessionId={session?.id}
      />

      {/* Findings, Commentary & Actions dialog — standalone layer, new
          Stocktake module only, does not touch the Summary Wizard */}
      <FindingsActionsDialog
        open={findingsDialogOpen}
        onOpenChange={setFindingsDialogOpen}
        sessionId={session?.id}
        sessionLocked={session?.status === 'locked'}
      />
    </div>
  );
}
